import { Filter, Search, Scale, Info, Trash2, X, Tag, Check, Lock, Archive, Loader2, Calendar, ChevronDown, Printer, Share2, Eye, LayoutGrid, CalendarDays, Activity, CheckCircle2, Layers, List, Clock, BellOff } from "lucide-react";
import jsPDF from "jspdf";
import { Link, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import CaseStatCards from "../components/crm/cases/CaseStatCards";
import ChipSelect from "../components/crm/ChipSelect";
import useAnchoredPosition from "../components/crm/useAnchoredPosition";
import { StatusPill } from "../components/crm/cases/CasePresentation";
import LabelManager from "../components/LabelManager";
import LabelList from "../components/LabelList";
import { db } from "../firebase";
import { collection, getDocs, query, where, deleteDoc, doc, writeBatch, setDoc, getDoc, updateDoc } from "firebase/firestore";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { getUserLabels, assignLabelsToCase } from "../services/labelService";
import featureGatingService from "../services/featureGatingService";
import { getStatusBucket } from "../utils/caseStatus";
import Pagination, { ITEMS_PER_PAGE } from "../components/Pagination";
import ShareCasesModal from "../components/ShareCasesModal";
import { setGlobalOrderNotifications } from "../services/fetchCaseApi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import React, { useState, useEffect, useMemo, useRef } from "react";
import { isCaseShared, useDisposeSharingPrompt } from "../components/DisposeSharingModal";


const PRINT_FIELD_GROUPS = [
  {
    title: "General",
    fields: [
      { key: "caseNumber", label: "Case No." },
      { key: "type", label: "Case Type" },
      { key: "status", label: "Status" },
      { key: "court", label: "Court Name" },
      { key: "caseFiledDate", label: "Date of Filing" },
      { key: "caseYear", label: "Case Year" },
      { key: "nextHearingDate", label: "Next Hearing Date" },
      { key: "previousHearingDate", label: "Previous Hearing Date" },
      { key: "partyContactNo", label: "Party Contact No." },
      { key: "stayOrderDate", label: "Stay Order Date" },
      { key: "counterAffidavitStatus", label: "Counter Affidavit Status" },
      { key: "counterAppealFiled", label: "C.A. Filed (Y/N)" },
      { key: "authorityDepartment", label: "Department", deptOnly: true },
      { key: "properties", label: "Khasra Nos./Plot Nos", deptOnly: true },
      { key: "area", label: "Area", deptOnly: true },
      { key: "village", label: "Village / Division", deptOnly: true },
    ],
  },
  {
    title: "Parties & Advocates",
    fields: [
      { key: "petitioner", label: "Petitioner" },
      { key: "petitionerAdvocate", label: "Petitioner Advocate" },
      { key: "respondent", label: "Respondent" },
      { key: "respondentAdvocate", label: "Respondent Advocate" },
    ],
  },
  {
    title: "Prayers & Remarks",
    fields: [
      { key: "prayers", label: "Prayers" },
      { key: "caseNotes", label: "Remarks" },
    ],
  },
];

const ALL_PRINT_FIELD_KEYS = PRINT_FIELD_GROUPS.flatMap((g) =>
  g.fields.map((f) => f.key)
);

const AUTHORITY_DEPARTMENTS = [
  "Residential",
  "Institutional",
  "IT",
  "HRD",
  "Water",
  "Urban Services",
  "Land",
  "Builders",
  "Group Housing",
  "Sewar",
  "Health",
  "University",
  "Finance",
  "Commercial",
  "6% Abadi Plot",
  "Others",
];

// Nishant's team-column look kept per request: uniform indigo overlapping
// avatars + "+N" overflow. Wired to real team data (unlike his dummy version).
const initialOfName = (s) => {
  const t = (s || "").trim();
  return t ? t.charAt(0).toUpperCase() : "?";
};
// Board-view avatars reuse the Shared Cases look: hashed pastel circles with a
// white ring, so both pages render the same card.
const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-violet-100 text-violet-700",
  "bg-rose-100 text-rose-700",
  "bg-sky-100 text-sky-700",
];
const colorFor = (seed) => {
  let h = 0;
  const s = seed || "";
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const initialsOf = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};
function TeamAvatarsNishant({ members = [], max = 3 }) {
  if (!members.length) return <span className="text-sm text-slate-400">—</span>;
  const shown = members.slice(0, max);
  const extra = members.length - shown.length;
  return (
    <div className="flex items-center">
      {shown.map((m, i) => (
        <span
          key={m.uid || i}
          title={m.name || m.uid}
          className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-indigo-100 text-[10px] font-bold text-indigo-700"
          style={{ marginLeft: i === 0 ? 0 : "-6px" }}
        >
          {initialOfName(m.name || m.uid)}
        </span>
      ))}
      {extra > 0 && (
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-slate-50 text-[10px] font-bold text-slate-500"
          style={{ marginLeft: "-6px" }}
        >
          +{extra}
        </span>
      )}
    </div>
  );
}

function AllCases() {

  const [isLightMode, setIsLightMode] = useState(
    localStorage.getItem("theme") === "light"
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsLightMode(
        localStorage.getItem("theme") === "light"
      );
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
    }, []);
  const [allCases, setAllCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [isLoadingCases, setIsLoadingCases] = useState(true);
  const { globalUser, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  // Order-notification preference (global, i.e. across all of this user's cases).
  // `notificationsEnabled` is absent by default and the backend treats absent as
  // on, so mirror that opt-out model here.
  const [orderNotificationsEnabled, setOrderNotificationsEnabled] = useState(true);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [isSavingNotificationPref, setIsSavingNotificationPref] = useState(false);
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const filterBtnRef = useRef(null);
  // Panel is w-72 wide regardless of the icon-sized trigger, so don't match it.
  const filterPanelPos = useAnchoredPosition(showFilterOptions, filterBtnRef, {
    matchTrigger: false,
    minWidth: 288,
    maxWidth: 288,
    maxHeight: 560,
  });
  const [searchField, setSearchField] = useState("all");
  const [caseYearFilter, setCaseYearFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedCases, setSelectedCases] = useState([]);
  // Share selection mode (pick cases, then share to teams/individuals)
  const [isShareSelectionMode, setIsShareSelectionMode] = useState(false);
  const [selectedCasesForShare, setSelectedCasesForShare] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDeleteAllConfirmation, setShowDeleteAllConfirmation] = useState(false);
  const [showDisposeAllConfirmation, setShowDisposeAllConfirmation] = useState(false);
  const [isDisposingAll, setIsDisposingAll] = useState(false);
  const [askKeepSharing, disposeSharingModal] = useDisposeSharingPrompt();
  const [labels, setLabels] = useState([]);
  const [showLabelManager, setShowLabelManager] = useState(false);
  const [labelFilter, setLabelFilter] = useState("all");
  const [hearingDateFilter, setHearingDateFilter] = useState("");
  const [disposedWithDirectionFilter, setDisposedWithDirectionFilter] = useState("all"); // "all" | "yes"
  const [stayOrderDateFilter, setStayOrderDateFilter] = useState(null);
  const [showLabelList, setShowLabelList] = useState(false);
  const [isLabelAssignmentMode, setIsLabelAssignmentMode] = useState(false);
  const [selectedLabelForBulkAssign, setSelectedLabelForBulkAssign] = useState(null);
  const [selectedCasesForLabel, setSelectedCasesForLabel] = useState([]);
  const [lockedCasesMap, setLockedCasesMap] = useState(new Map());
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [lockedCaseClicked, setLockedCaseClicked] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [expandedFilterSections, setExpandedFilterSections] = useState({});
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printFormat, setPrintFormat] = useState("pdf"); // "pdf" | "excel"
  const [printScope, setPrintScope] = useState("pending"); // "pending" | "disposed" | "all" | "selected"
  const [isPrintSelectionMode, setIsPrintSelectionMode] = useState(false);
  const [selectedCasesForPrint, setSelectedCasesForPrint] = useState([]);
  const [isDepartmentVerified, setIsDepartmentVerified] = useState(false);
  const [selectedAuthorityDepartments, setSelectedAuthorityDepartments] = useState([]);
  const [showAuthorityDeptModal, setShowAuthorityDeptModal] = useState(false);
  const [tempAuthorityDepartments, setTempAuthorityDepartments] = useState([]);

  // ── New (light CRM redesign) ──────────────────────────────────────────────
  // Chip filters layered on top of the existing `filteredCases` result set, a
  // Table/Board view switch, the open row-actions menu, and a lookup
  // of team-member uid → display name (for the Team avatar column).
  const [courtFilter, setCourtFilter] = useState("all");
  const [caseTypeFilter, setCaseTypeFilter] = useState("all");
  const [advocateFilter, setAdvocateFilter] = useState("all");
  const [viewMode, setViewMode] = useState("table"); // "table" | "board"
  const [memberNames, setMemberNames] = useState({});
  const fetchedMembersRef = useRef(new Set());

  // Department filter options: the fixed list plus any free-text departments
  // that actually exist in the user's cases (e.g. values brought in via the
  // Excel import that aren't part of the predefined taxonomy). Without this,
  // imported cases can never be selected/matched in the department filter.
  const departmentOptions = useMemo(() => {
    const seen = new Set(AUTHORITY_DEPARTMENTS.map((d) => d.toLowerCase()));
    const extras = [];
    (allCases || []).forEach((c) => {
      const dept = String(c.authorityDepartment || "").trim();
      if (!dept) return;
      const key = dept.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      extras.push(dept);
    });
    extras.sort((a, b) => a.localeCompare(b));
    return [...AUTHORITY_DEPARTMENTS, ...extras];
  }, [allCases]);
  const [selectedPrintFields, setSelectedPrintFields] = useState(
    () => new Set(ALL_PRINT_FIELD_KEYS)
  );
  const toggleFilterSection = (key) =>
    setExpandedFilterSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // ── Derived data for the redesigned UI ────────────────────────────────────
  // Parse a "DD/MM/YYYY" or "DD-MM-YYYY" hearing date into a midnight Date.
  const parseHearingDate = (str) => {
    if (!str) return null;
    const sep = str.includes("/") ? "/" : "-";
    const [d, m, y] = str.split(sep).map(Number);
    if (!d || !m || !y) return null;
    const dt = new Date(y, m - 1, d);
    dt.setHours(0, 0, 0, 0);
    return dt;
  };

  const todayStart = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  const pad2 = (n) => String(n).padStart(2, "0");
  const todayISO = `${todayStart.getFullYear()}-${pad2(
    todayStart.getMonth() + 1
  )}-${pad2(todayStart.getDate())}`;

  // Stat-card counts. Pending/Closed use the existing status buckets; Today's
  // Hearings = pending cases whose next hearing is today; In Progress = pending
  // cases with a future hearing scheduled.
  const statCounts = useMemo(() => {
    const list = allCases || [];
    let pending = 0;
    let closed = 0;
    let today = 0;
    let inProgress = 0;
    list.forEach((c) => {
      if (getStatusBucket(c.status) === "disposed") {
        closed++;
        return;
      }
      pending++;
      const hd = parseHearingDate(c.nextHearingDate);
      if (hd) {
        if (hd.getTime() === todayStart.getTime()) today++;
        else if (hd.getTime() > todayStart.getTime()) inProgress++;
      }
    });
    return { total: list.length, pending, closed, today, inProgress };
  }, [allCases, todayStart]);

  // Distinct, sorted option lists for the Court / Case Type / Advocate chips.
  const uniqSorted = (values) => {
    const s = new Set();
    values.forEach((v) => {
      const t = (v || "").toString().trim();
      if (t) s.add(t);
    });
    return [...s].sort((a, b) => a.localeCompare(b));
  };
  const courtOptions = useMemo(
    () => uniqSorted((allCases || []).map((c) => c.court)),
    [allCases]
  );
  const caseTypeOptions = useMemo(
    () => uniqSorted((allCases || []).map((c) => c.type)),
    [allCases]
  );
  const advocateOptions = useMemo(() => {
    const s = new Set();
    (allCases || []).forEach((c) => {
      [c.petitionerAdvocate, c.respondentAdvocate].forEach((a) => {
        if (a && a.trim()) s.add(a.trim());
      });
      (c.allPetitionerAdvocates || []).forEach((a) => {
        if (a?.name?.trim()) s.add(a.name.trim());
      });
      (c.allRespondentAdvocates || []).forEach((a) => {
        if (a?.name?.trim()) s.add(a.name.trim());
      });
    });
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [allCases]);

  const caseMatchesAdvocate = (c, adv) => {
    const names = [
      c.petitionerAdvocate,
      c.respondentAdvocate,
      ...(c.allPetitionerAdvocates || []).map((a) => a?.name),
      ...(c.allRespondentAdvocates || []).map((a) => a?.name),
    ];
    return names.some((n) => (n || "").trim() === adv);
  };

  // Chip filters layered on top of the existing `filteredCases` result set so
  // none of the battle-tested search/filter logic above has to change.
  const visibleCases = useMemo(() => {
    let list = filteredCases || [];
    if (courtFilter !== "all")
      list = list.filter((c) => (c.court || "").trim() === courtFilter);
    if (caseTypeFilter !== "all")
      list = list.filter((c) => (c.type || "").trim() === caseTypeFilter);
    if (advocateFilter !== "all")
      list = list.filter((c) => caseMatchesAdvocate(c, advocateFilter));
    return list;
  }, [filteredCases, courtFilter, caseTypeFilter, advocateFilter]);

  // Resolve a case's team uids → [{ uid, name }] for the avatar column.
  const resolveMembers = (caseItem) => {
    const seen = new Set();
    const out = [];
    (caseItem.team || []).forEach((u) => {
      if (u && !seen.has(u)) {
        seen.add(u);
        out.push({ uid: u, name: memberNames[u] || "" });
      }
    });
    return out;
  };

  const hasActiveFilters =
    Boolean(searchQuery) ||
    statusFilter !== "all" ||
    courtFilter !== "all" ||
    caseTypeFilter !== "all" ||
    advocateFilter !== "all" ||
    labelFilter !== "all" ||
    Boolean(hearingDateFilter) ||
    Boolean(caseYearFilter) ||
    Boolean(stayOrderDateFilter) ||
    disposedWithDirectionFilter === "yes" ||
    selectedAuthorityDepartments.length > 0;

  const clearAllFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setCourtFilter("all");
    setCaseTypeFilter("all");
    setAdvocateFilter("all");
    setLabelFilter("all");
    setHearingDateFilter("");
    setCaseYearFilter("");
    setStayOrderDateFilter(null);
    setDisposedWithDirectionFilter("all");
    setSelectedAuthorityDepartments([]);
    setSearchField("all");
  };

  // Resolve team-member display names (uid → name) from lawyers, then client.
  // Already-fetched uids are tracked in a ref so this only fetches new members
  // when the case set grows — no dependency on the names state itself.
  useEffect(() => {
    const uids = new Set();
    (allCases || []).forEach((c) =>
      (c.team || []).forEach((u) => {
        if (u) uids.add(u);
      })
    );
    const missing = [...uids]
      .filter((u) => !fetchedMembersRef.current.has(u))
      .slice(0, 60);
    if (missing.length === 0) return;
    missing.forEach((u) => fetchedMembersRef.current.add(u));
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        missing.map(async (uid) => {
          try {
            let snap = await getDoc(doc(db, "lawyers", uid));
            if (!snap.exists()) snap = await getDoc(doc(db, "client", uid));
            const data = snap.exists() ? snap.data() : {};
            return [uid, data.name || data.displayName || ""];
          } catch {
            return [uid, ""];
          }
        })
      );
      if (cancelled) return;
      setMemberNames((prev) => {
        const next = { ...prev };
        entries.forEach(([u, nm]) => {
          next[u] = nm;
        });
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [allCases]);

  // const filterIconRef = useRef(null); // For more complex positioning if needed

  const isUserAuthenticated = globalUser !== null;
  const isClient = localStorage.getItem("userType") === "client";

  // Fetch labels from Firestore
  useEffect(() => {
    const fetchLabels = async () => {
      if (!isUserAuthenticated) return;

      try {
        const userLabels = await getUserLabels(globalUser.uid);
        setLabels(userLabels);
      } catch (error) {
        console.error("Error fetching labels:", error);
      }
    };

    fetchLabels();
  }, [globalUser, isUserAuthenticated]);

  // Load the user's global order-notification preference so the toolbar icon and
  // the modal reflect the current state rather than assuming a default.
  useEffect(() => {
    const fetchNotificationPreference = async () => {
      if (!isUserAuthenticated) return;

      try {
        const userRef = doc(db, isClient ? "client" : "lawyers", globalUser.uid);
        const snap = await getDoc(userRef);
        // Absent means on — the backend's resolveNotificationEnabled() treats a
        // missing flag as enabled.
        setOrderNotificationsEnabled(snap.data()?.notificationsEnabled !== false);
      } catch (error) {
        console.error("Error fetching notification preference:", error);
      }
    };

    fetchNotificationPreference();
  }, [globalUser, isUserAuthenticated, isClient]);

  const handleToggleOrderNotifications = async () => {
    const next = !orderNotificationsEnabled;
    setIsSavingNotificationPref(true);

    try {
      await setGlobalOrderNotifications(globalUser.uid, next);
      setOrderNotificationsEnabled(next);
      setShowNotificationModal(false);
      toast.success(
        next
          ? "Order notifications enabled for all your cases."
          : "Order notifications disabled for all your cases."
      );
    } catch (error) {
      console.error("Error updating notification preference:", error);
      toast.error(
        error?.response?.data?.message ||
          "Could not update notification setting. Please try again."
      );
    } finally {
      setIsSavingNotificationPref(false);
    }
  };

  // Fetch user's department verification flag
  useEffect(() => {
    const fetchDeptVerification = async () => {
      if (!isUserAuthenticated) {
        setIsDepartmentVerified(false);
        return;
      }
      try {
        const userCollectionName =
          localStorage.getItem("userType") === "client" ? "client" : "lawyers";
        const userDoc = await getDoc(doc(db, userCollectionName, globalUser.uid));
        if (userDoc.exists()) {
          setIsDepartmentVerified(Boolean(userDoc.data().departmentVerified));
        } else {
          setIsDepartmentVerified(false);
        }
      } catch (error) {
        console.error("Error fetching department verification:", error);
        setIsDepartmentVerified(false);
      }
    };
    fetchDeptVerification();
  }, [globalUser, isUserAuthenticated]);

  // Fetch cases from Firestore
  useEffect(() => {
    const fetchCases = async () => {
      if (!isUserAuthenticated) {
        setIsLoadingCases(false);
        return;
      }
      setIsLoadingCases(true);

      let caseList = [];
      const pendingCollection = collection(db, "pending");
      const disposedCollection = collection(db, "disposed");

      // Organisation-group case sharing: read the current user's sharing groups
      // so we can also pull cases tagged with those groupIds.
      let sharingGroups = [];
      try {
        const uType =
          localStorage.getItem("userType") === "client" ? "client" : "lawyers";
        const uDoc = await getDoc(doc(db, uType, globalUser.uid));
        if (uDoc.exists() && Array.isArray(uDoc.data().sharingGroups)) {
          sharingGroups = uDoc.data().sharingGroups;
        }
      } catch (e) {
        console.error("Error reading sharingGroups:", e);
      }
      // Firestore "in" allows at most 30 values.
      const myGroups = sharingGroups.slice(0, 30);
      if (sharingGroups.length > 30) {
        console.warn(
          `User in ${sharingGroups.length} sharing groups; only first 30 queried.`
        );
      }

      const ownedPendingCasesQuery = query(pendingCollection, where("owner", "==", globalUser.uid));
      const ownedDisposedCasesQuery = query(disposedCollection, where("owner", "==", globalUser.uid));
      const sharedPendingCasesQuery = query(pendingCollection, where("savedByTeamMember", "array-contains", globalUser.uid));
      const sharedDisposedCasesQuery = query(disposedCollection, where("savedByTeamMember", "array-contains", globalUser.uid));

      // For clients: also fetch cases shared via team array (synced with lawyer's data)
      const clientUserType = localStorage.getItem("userType");
      const isClientUser = clientUserType === "client";
      let teamPendingCasesQuery = null;
      let teamDisposedCasesQuery = null;
      if (isClientUser) {
        teamPendingCasesQuery = query(pendingCollection, where("team", "array-contains", globalUser.uid));
        teamDisposedCasesQuery = query(disposedCollection, where("team", "array-contains", globalUser.uid));
      }

      const pendingDocs = [];
      const seenIds = new Set();
      const processSnapshot = (snapshot, collectionName, isShared = false) => {
        snapshot.forEach((doc) => {
          if (seenIds.has(doc.id)) return;
          seenIds.add(doc.id);
          if (collectionName === "pending") {
            pendingDocs.push(doc);
          }
          const caseData = doc.data();
          caseList.push({
            id: doc.id,
            collection: collectionName,
            caseNumber: caseData.caseNo,
            type: caseData.caseType,
            petitionerName: caseData.petitionerName,
            respondentName: caseData.respondentName,
            court: caseData.courtName,
            status: caseData.status || "Status not available",
            nextHearingDate: caseData.nextHearingDate || null,
            labels: caseData.labels || [],
            labelsByUser: caseData.labelsByUser || null,
            // Incremented by the hc-services case-sync cron when the High Court
            // portal publishes new orders; cleared when the user opens the case.
            newOrdersCount: caseData.newOrdersCount || 0,
            timestamp: caseData.timestamp || null,
            isShared: isShared,
            owner: caseData.owner,
            team: Array.isArray(caseData.team) ? caseData.team : [],
            disposedWithDirection: caseData.disposedWithDirection || false,
            stayOrderDate: caseData.stayOrderDate || null,
            authorityDepartment: caseData.authorityDepartment || "",
            authorityDepartmentOther: caseData.authorityDepartmentOther || "",
            counterAffidavitStatus: caseData.counterAffidavitStatus || "",
            caseFiledDate: caseData.caseFiledDate || "",
            village: caseData.village || "",
            properties: caseData.properties || "",
            area: caseData.area || "",
            counterAppealFiled: caseData.counterAppealFiled || "",
            previousHearingDate: caseData.previousHearingDate || "",
            petitionerAdvocate: caseData.petitionerAdvocate || "",
            respondentAdvocate: caseData.respondentAdvocate || "",
            caseNotes: caseData.casenotes || caseData.caseNotes || "",
            caseYear: caseData.caseYear || "",
            appearingFor: caseData.appearingFor || "",
            partyContactNo: caseData.partyContactNo || "",
            petitionerRelation: caseData.petitionerRelation || "",
            petitionerRelationName: caseData.petitionerRelationName || "",
            respondentRelation: caseData.respondentRelation || "",
            respondentRelationName: caseData.respondentRelationName || "",
            allPetitioners: Array.isArray(caseData.allPetitioners) ? caseData.allPetitioners : [],
            allRespondents: Array.isArray(caseData.allRespondents) ? caseData.allRespondents : [],
            allPetitionerAdvocates: Array.isArray(caseData.allPetitionerAdvocates) ? caseData.allPetitionerAdvocates : [],
            allRespondentAdvocates: Array.isArray(caseData.allRespondentAdvocates) ? caseData.allRespondentAdvocates : [],
            prayers: caseData.prayers || "",
            payload: caseData.payload || null,
          });
        });
      };

      // Build the set of queries as descriptors so ordering stays correct as
      // we add optional client / group queries.
      const queryJobs = [
        { q: ownedPendingCasesQuery, collection: "pending", isShared: false },
        { q: ownedDisposedCasesQuery, collection: "disposed", isShared: false },
        { q: sharedPendingCasesQuery, collection: "pending", isShared: true },
        { q: sharedDisposedCasesQuery, collection: "disposed", isShared: true },
      ];
      if (isClientUser && teamPendingCasesQuery && teamDisposedCasesQuery) {
        queryJobs.push({ q: teamPendingCasesQuery, collection: "pending", isShared: true });
        queryJobs.push({ q: teamDisposedCasesQuery, collection: "disposed", isShared: true });
      }
      // Organisation-group shared cases (read-time by sharedGroupId)
      if (myGroups.length > 0) {
        queryJobs.push({
          q: query(pendingCollection, where("sharedGroupId", "in", myGroups)),
          collection: "pending",
          isShared: true,
        });
        queryJobs.push({
          q: query(disposedCollection, where("sharedGroupId", "in", myGroups)),
          collection: "disposed",
          isShared: true,
        });
      }

      const snapshots = await Promise.all(queryJobs.map((j) => getDocs(j.q)));
      snapshots.forEach((snap, i) =>
        processSnapshot(snap, queryJobs[i].collection, queryJobs[i].isShared)
      );

      // Deduplication already handled by seenIds set in processSnapshot

      // Sort cases by timestamp (newest first) — show immediately before lock check
      caseList.sort((a, b) => {
        const aTime = a.timestamp?.toMillis ? a.timestamp.toMillis() : (a.timestamp || 0);
        const bTime = b.timestamp?.toMillis ? b.timestamp.toMillis() : (b.timestamp || 0);
        if (aTime && bTime) return bTime - aTime;
        if (aTime && !bTime) return -1;
        if (!aTime && bTime) return 1;
        return 0;
      });

      // Show cases immediately — don't wait for lock check
      setAllCases(caseList);
      setFilteredCases(caseList);
      setIsLoadingCases(false);

      // Check lock status in the background (non-blocking)
      if (pendingDocs.length > 0) {
        try {
          const lockStatusMap = await featureGatingService.getLockedCasesMap(globalUser.uid, pendingDocs);
          setLockedCasesMap(lockStatusMap);
        } catch (error) {
          console.error('Error checking lock status:', error);
        }
      }
    };

    fetchCases();
  }, [globalUser, isUserAuthenticated, refreshKey]);

  // Filter cases based on search query, status, and labels
  useEffect(() => {
    let filtered = allCases || [];

    if (statusFilter !== "all") {
      filtered = filtered.filter((caseItem) => getStatusBucket(caseItem.status) === statusFilter);
    }

    if (labelFilter !== "all") {
      filtered = filtered.filter((caseItem) =>
        caseItem.labels && caseItem.labels.includes(labelFilter)
      );
    }

    if (hearingDateFilter) {
      const selectedDate = hearingDateFilter; // yyyy-mm-dd from input
      filtered = filtered.filter((caseItem) => {
        if (!caseItem.nextHearingDate) return false;
        const sep = caseItem.nextHearingDate.includes('/') ? '/' : '-';
        const [day, month, year] = caseItem.nextHearingDate.split(sep).map(Number);
        const formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return formattedDate === selectedDate;
      });
    }

    if (disposedWithDirectionFilter === "yes") {
      filtered = filtered.filter((c) => Boolean(c.disposedWithDirection));
    }

    if (stayOrderDateFilter) {
      const day = String(stayOrderDateFilter.getDate()).padStart(2, "0");
      const month = String(stayOrderDateFilter.getMonth() + 1).padStart(2, "0");
      const year = stayOrderDateFilter.getFullYear();
      const target = `${day}/${month}/${year}`;
      filtered = filtered.filter((c) => c.stayOrderDate === target);
    }

    if (selectedAuthorityDepartments.length > 0) {
      // Match case-insensitively / trimmed so Excel-imported departments
      // (free text like "Property ") still match the selected options.
      const deptSet = new Set(
        selectedAuthorityDepartments.map((d) => String(d).trim().toLowerCase())
      );
      filtered = filtered.filter((item) =>
        deptSet.has(String(item.authorityDepartment || "").trim().toLowerCase())
      );
    }

    if (searchQuery) {
      const normalizedQuery = searchQuery.toLowerCase();
      // Department / village / khasra / area are organisation-account fields
      // (gated behind departmentVerified). Let the general search match them
      // too, so any word also looks inside Village/Division, Khasra/Plot, Area.
      const matchesDeptFields = (item) =>
        isDepartmentVerified &&
        ((item.authorityDepartment === "Others"
          ? item.authorityDepartmentOther
          : item.authorityDepartment
        )?.toLowerCase().includes(normalizedQuery) ||
          item.properties?.toLowerCase().includes(normalizedQuery) ||
          item.area?.toLowerCase().includes(normalizedQuery) ||
          item.village?.toLowerCase().includes(normalizedQuery));
      if (searchField === "caseNumber") {
        filtered = filtered.filter((item) =>
          item.caseNumber?.toLowerCase().includes(normalizedQuery) ||
          item.payload?.caseNumber?.toLowerCase().includes(normalizedQuery) ||
          item.payload?.diaryNumber?.toLowerCase().includes(normalizedQuery) ||
          String(item.caseYear || "").toLowerCase().includes(normalizedQuery)
        );
      } else if (searchField === "authorityDepartment") {
        filtered = filtered.filter((item) => {
          const dept = item.authorityDepartment === "Others"
            ? item.authorityDepartmentOther
            : item.authorityDepartment;
          return (
            dept?.toLowerCase().includes(normalizedQuery) ||
            item.properties?.toLowerCase().includes(normalizedQuery) ||
            item.area?.toLowerCase().includes(normalizedQuery) ||
            item.village?.toLowerCase().includes(normalizedQuery)
          );
        });
      } else if (searchField === "counterAffidavitStatus") {
        filtered = filtered.filter((item) =>
          item.counterAffidavitStatus?.toLowerCase().includes(normalizedQuery)
        );
      } else if (disposedWithDirectionFilter === "yes") {
        filtered = filtered.filter(
          (item) =>
            item.caseNumber?.toLowerCase().includes(normalizedQuery) ||
            item.payload?.caseNumber?.toLowerCase().includes(normalizedQuery) ||
            item.payload?.diaryNumber?.toLowerCase().includes(normalizedQuery) ||
            item.filingNumber?.toLowerCase().includes(normalizedQuery) ||
            String(item.caseYear || "").toLowerCase().includes(normalizedQuery) ||
            item.type?.toLowerCase().includes(normalizedQuery) ||
            item.petitionerName?.toLowerCase().includes(normalizedQuery) ||
            item.respondentName?.toLowerCase().includes(normalizedQuery) ||
            item.court?.toLowerCase().includes(normalizedQuery) ||
            item.petitionerAdvocate?.toLowerCase().includes(normalizedQuery) ||
            item.respondentAdvocate?.toLowerCase().includes(normalizedQuery) ||
            (Array.isArray(item.allPetitionerAdvocates) &&
              item.allPetitionerAdvocates.some((a) =>
                a?.name?.toLowerCase().includes(normalizedQuery)
              )) ||
            (Array.isArray(item.allRespondentAdvocates) &&
              item.allRespondentAdvocates.some((a) =>
                a?.name?.toLowerCase().includes(normalizedQuery)
              )) ||
            item.directionNotes?.toLowerCase().includes(normalizedQuery) ||
            matchesDeptFields(item)
        );
      } else {
        filtered = filtered.filter(
          (item) =>
            item.caseNumber?.toLowerCase().includes(normalizedQuery) ||
            item.payload?.caseNumber?.toLowerCase().includes(normalizedQuery) ||
            item.payload?.diaryNumber?.toLowerCase().includes(normalizedQuery) ||
            item.filingNumber?.toLowerCase().includes(normalizedQuery) ||
            String(item.caseYear || "").toLowerCase().includes(normalizedQuery) ||
            item.type?.toLowerCase().includes(normalizedQuery) ||
            item.petitionerName?.toLowerCase().includes(normalizedQuery) ||
            item.respondentName?.toLowerCase().includes(normalizedQuery) ||
            item.court?.toLowerCase().includes(normalizedQuery) ||
            item.petitionerAdvocate?.toLowerCase().includes(normalizedQuery) ||
            item.respondentAdvocate?.toLowerCase().includes(normalizedQuery) ||
            (Array.isArray(item.allPetitionerAdvocates) &&
              item.allPetitionerAdvocates.some((a) =>
                a?.name?.toLowerCase().includes(normalizedQuery)
              )) ||
            (Array.isArray(item.allRespondentAdvocates) &&
              item.allRespondentAdvocates.some((a) =>
                a?.name?.toLowerCase().includes(normalizedQuery)
              )) ||
            matchesDeptFields(item)
        );
      }
    }

    if (caseYearFilter) {
      const yearStr = String(caseYearFilter);
      filtered = filtered.filter((item) => {
        // Prefer the dedicated caseYear field (set by import + manual add).
        // Fall back to matching the year inside the case number string for
        // older cases that never stored caseYear separately.
        const cy = String(item.caseYear || "");
        if (cy) return cy === yearStr;
        const cn = item.caseNumber || "";
        const pcn = item.payload?.caseNumber || "";
        if (!cn && !pcn) return false;
        return cn.includes(yearStr) || pcn.includes(yearStr);
      });
    }

    // Sort order:
    //   1. Lock status (PRIMARY) — all unlocked cases appear above all locked ones,
    //      regardless of when they were added, so locked cases never interleave across pages.
    //   2. Addition date (SECONDARY) — within each group, newest first.
    filtered.sort((a, b) => {
      const aIsLocked = (a.collection === "pending" && lockedCasesMap.get(a.id)?.isLocked) || false;
      const bIsLocked = (b.collection === "pending" && lockedCasesMap.get(b.id)?.isLocked) || false;

      // Primary: unlocked before locked
      if (aIsLocked !== bIsLocked) return aIsLocked ? 1 : -1;

      // Secondary: newest first by timestamp
      const aTime = a.timestamp?.toMillis ? a.timestamp.toMillis() : (a.timestamp || 0);
      const bTime = b.timestamp?.toMillis ? b.timestamp.toMillis() : (b.timestamp || 0);

      if (aTime && bTime) return bTime - aTime;
      if (aTime && !bTime) return -1;
      if (!aTime && bTime) return 1;

      return 0;
    });

    setFilteredCases(filtered);
  }, [searchQuery, allCases, searchField, statusFilter, labelFilter, hearingDateFilter, caseYearFilter, lockedCasesMap, disposedWithDirectionFilter, stayOrderDateFilter, selectedAuthorityDepartments, isDepartmentVerified]);

  // Reset to page 1 when user changes search/filter
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, searchField, statusFilter, labelFilter, hearingDateFilter, caseYearFilter, disposedWithDirectionFilter, stayOrderDateFilter, selectedAuthorityDepartments, courtFilter, caseTypeFilter, advocateFilter]);

  // Helper function to check if next hearing date is today, null, or in the past
  const shouldShowUpdateBanner = (nextHearingDate, status) => {
    // Don't show banner for disposed cases
    if (getStatusBucket(status) === "disposed") return false;

    // Show banner if date is null/empty
    if (!nextHearingDate) return true;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    // Parse the date string (format: DD/MM/YYYY or DD-MM-YYYY)
    const separator = nextHearingDate.includes('/') ? '/' : '-';
    const [day, month, year] = nextHearingDate.split(separator).map(Number);
    if (!day || !month || !year) return true;
    const hearingDate = new Date(year, month - 1, day); // month is 0-indexed
    hearingDate.setHours(0, 0, 0, 0);

    // Show banner if hearing date is today or in the past
    return hearingDate <= today;
  };

  // Helper function to format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return dateStr;
  };

  // Helper function to truncate party names equally
  const formatPartyNames = (petitioner, respondent, maxLength = 20) => {
    if (!petitioner && !respondent) return "N/A";
    if (!petitioner) return respondent;
    if (!respondent) return petitioner;

    const halfLength = Math.floor(maxLength / 2);

    const truncateName = (name, length) => {
      if (name.length <= length) return name;
      return name.substring(0, length) + "...";
    };

    const petitionerTruncated = truncateName(petitioner, halfLength);
    const respondentTruncated = truncateName(respondent, halfLength);

    return `${petitionerTruncated} Vs ${respondentTruncated}`;
  };

  // Get label object by ID
  const getLabelById = (labelId) => {
    return labels.find((label) => label.id === labelId);
  };

  // Handle label selection for bulk assignment
  const handleLabelSelectForBulk = (label) => {
    setSelectedLabelForBulkAssign(label);
    setIsLabelAssignmentMode(true);
    setShowLabelList(false);
    setSelectedCasesForLabel([]);
  };

  // Handle bulk label assignment
  const handleBulkLabelAssign = async () => {
    if (!selectedLabelForBulkAssign || selectedCasesForLabel.length === 0) return;

    try {
      // For each selected case, get its current labels and add the new label
      const updatePromises = selectedCasesForLabel.map(async (caseItem) => {
        const currentLabels = caseItem.labels || [];
        // Only add if not already present
        const newLabels = currentLabels.includes(selectedLabelForBulkAssign.id)
          ? currentLabels
          : [...currentLabels, selectedLabelForBulkAssign.id];

        await assignLabelsToCase(caseItem.collection, caseItem.id, newLabels);
        return { id: caseItem.id, labels: newLabels };
      });

      const results = await Promise.all(updatePromises);

      // Update local state
      setAllCases((prevCases) =>
        prevCases.map((c) => {
          const updated = results.find((r) => r.id === c.id);
          return updated ? { ...c, labels: updated.labels } : c;
        })
      );

      // Reset state
      setIsLabelAssignmentMode(false);
      setSelectedLabelForBulkAssign(null);
      setSelectedCasesForLabel([]);
    } catch (error) {
      console.error("Error assigning labels:", error);
      alert("Failed to assign labels. Please try again.");
    }
  };

  // Cancel bulk label assignment
  const handleCancelBulkAssign = () => {
    setIsLabelAssignmentMode(false);
    setSelectedLabelForBulkAssign(null);
    setSelectedCasesForLabel([]);
  };

  // Helper function to delete events and cancel reminders for a case
  const cleanupEventsAndReminders = async (caseNumber, userId) => {
    if (!caseNumber || !userId) {
      console.log("Missing caseNumber or userId for cleanup");
      return;
    }

    console.log(`Cleaning up events and reminders for case: ${caseNumber}`);

    try {
      const batch = writeBatch(db);
      let eventsDeleted = 0;
      let remindersDeleted = 0;

      // 1. Delete events from lawyers/{uid}/events where "Case No" matches
      const eventsQuery = query(
        collection(db, "lawyers", userId, "events"),
        where("Case No", "==", caseNumber)
      );
      const eventsSnapshot = await getDocs(eventsQuery);

      eventsSnapshot.docs.forEach((eventDoc) => {
        batch.delete(eventDoc.ref);
        eventsDeleted++;
      });

      // 2. Delete/cancel reminders from eventReminders where caseNo matches
      const remindersQuery = query(
        collection(db, "eventReminders"),
        where("caseNo", "==", caseNumber),
        where("recipientId", "==", userId)
      );
      const remindersSnapshot = await getDocs(remindersQuery);

      remindersSnapshot.docs.forEach((reminderDoc) => {
        batch.delete(reminderDoc.ref);
        remindersDeleted++;
      });

      // Commit the batch
      if (eventsDeleted > 0 || remindersDeleted > 0) {
        await batch.commit();
        console.log(`Cleanup complete: ${eventsDeleted} events deleted, ${remindersDeleted} reminders deleted`);
      } else {
        console.log("No events or reminders found to clean up");
      }
    } catch (error) {
      console.error("Error cleaning up events and reminders:", error);
    }
  };

  // Handle deletion of selected cases
  const handleDelete = async () => {
    setShowConfirmation(false);
    // Filter out shared cases — clients should not delete lawyer's cases
    const ownedSelectedCases = selectedCases.filter((c) => {
      const fullCase = allCases.find((ac) => ac.id === c.id);
      return !fullCase?.isShared;
    });
    const pendingToDelete = ownedSelectedCases.filter((c) => c.collection === "pending").map((c) => c.id);
    const disposedToDelete = ownedSelectedCases.filter((c) => c.collection === "disposed").map((c) => c.id);

    try {
      // Get case numbers for cleanup before deleting
      const casesToCleanup = selectedCases.map((selectedCase) => {
        const fullCase = allCases.find((c) => c.id === selectedCase.id);
        return fullCase?.caseNumber;
      }).filter(Boolean);

      // Clean up events and reminders for each case
      if (globalUser?.uid && casesToCleanup.length > 0) {
        await Promise.all(
          casesToCleanup.map((caseNumber) =>
            cleanupEventsAndReminders(caseNumber, globalUser.uid)
          )
        );
      }

      if (pendingToDelete.length > 0) {
        await Promise.all(pendingToDelete.map((id) => deleteDoc(doc(db, "pending", id))));
      }

      if (disposedToDelete.length > 0) {
        await Promise.all(disposedToDelete.map((id) => deleteDoc(doc(db, "disposed", id))));
      }

      setAllCases((prevCases) => prevCases.filter((c) => !selectedCases.some((s) => s.id === c.id)));
      setSelectedCases([]);
      setIsSelectionMode(false);
    } catch (error) {
      console.error("Error deleting cases: ", error);
    }
  };

  // Handle deletion of ALL cases
  const handleDeleteAll = async () => {
    setShowDeleteAllConfirmation(false);
    // Only delete owned cases, not shared ones
    const ownedCases = allCases.filter((c) => !c.isShared);
    const pendingToDelete = ownedCases.filter((c) => c.collection === "pending").map((c) => c.id);
    const disposedToDelete = ownedCases.filter((c) => c.collection === "disposed").map((c) => c.id);

    try {
      // Clean up events and reminders for each owned case
      if (globalUser?.uid && ownedCases.length > 0) {
        await Promise.all(
          ownedCases
            .map((c) => c.caseNumber)
            .filter(Boolean)
            .map((caseNumber) => cleanupEventsAndReminders(caseNumber, globalUser.uid))
        );
      }

      if (pendingToDelete.length > 0) {
        await Promise.all(pendingToDelete.map((id) => deleteDoc(doc(db, "pending", id))));
      }
      if (disposedToDelete.length > 0) {
        await Promise.all(disposedToDelete.map((id) => deleteDoc(doc(db, "disposed", id))));
      }

      // Keep shared cases in the list, remove only owned ones
      const remainingSharedCases = allCases.filter((c) => c.isShared);
      setAllCases(remainingSharedCases);
      setFilteredCases(remainingSharedCases);
      setSelectedCases([]);
      setIsSelectionMode(false);
    } catch (error) {
      console.error("Error deleting all cases: ", error);
    }
  };

  // Handle dispose all pending cases
  const handleDisposeAll = async () => {
    setShowDisposeAllConfirmation(false);
    setIsDisposingAll(true);

    const pendingCasesToDispose = allCases.filter(
      (c) => c.collection === "pending" && !c.isShared
    );

    if (pendingCasesToDispose.length === 0) {
      toast.error("No pending cases to dispose.");
      setIsDisposingAll(false);
      return;
    }

    try {
      // Shared cases in the batch: ask once whether sharing survives disposal.
      const sharedCount = pendingCasesToDispose.filter((c) =>
        isCaseShared(c, globalUser?.uid)
      ).length;
      let keepSharing = false;
      if (sharedCount > 0) {
        const choice = await askKeepSharing(sharedCount);
        if (choice === null) return; // finally clears isDisposingAll
        keepSharing = choice;
      }

      for (const caseItem of pendingCasesToDispose) {
        const pendingCaseDoc = doc(db, "pending", caseItem.id);
        const disposedCaseDoc = doc(db, "disposed", caseItem.id);
        const pendingCaseSnapshot = await getDoc(pendingCaseDoc);

        if (pendingCaseSnapshot.exists()) {
          const caseDataToDispose = pendingCaseSnapshot.data();

          // Clean up events and reminders
          if (caseDataToDispose.caseNo && globalUser?.uid) {
            await cleanupEventsAndReminders(caseDataToDispose.caseNo, globalUser.uid);
          }

          // Remove shared members from default team if no other pending cases
          const sharedMembers = Array.isArray(caseDataToDispose.team)
            ? caseDataToDispose.team.filter((uid) => uid !== globalUser?.uid)
            : [];
          // Keep sharing: members stay on the case, so leave the default team alone.
          for (const memberUid of keepSharing ? [] : sharedMembers) {
            const otherPendingQuery = query(
              collection(db, "pending"),
              where("owner", "==", globalUser.uid),
              where("team", "array-contains", memberUid)
            );
            const otherPendingSnap = await getDocs(otherPendingQuery);
            const otherCases = otherPendingSnap.docs.filter((d) => d.id !== caseItem.id);
            if (otherCases.length === 0) {
              await featureGatingService.removeMemberFromDefaultTeam(globalUser.uid, memberUid);
            }
          }

          // Clear team on pending doc first
          if (!keepSharing && sharedMembers.length > 0) {
            await updateDoc(pendingCaseDoc, { team: [] });
          }

          // Move to disposed collection
          await setDoc(disposedCaseDoc, {
            ...caseDataToDispose,
            status: "disposed",
            // Keep sharing → carry `team` over so the disposed case stays shared.
            team: keepSharing ? caseDataToDispose.team || [] : [],
            previousHearingDate: caseDataToDispose.nextHearingDate || caseDataToDispose.previousHearingDate,
            nextHearingDate: null,
          });
          await deleteDoc(pendingCaseDoc);
        }
      }

      // Update local state - move pending cases to disposed
      const disposedIds = new Set(pendingCasesToDispose.map((c) => c.id));
      const updatedCases = allCases.map((c) => {
        if (disposedIds.has(c.id)) {
          return {
            ...c,
            status: "disposed",
            collection: "disposed",
            nextHearingDate: null,
            ...(keepSharing ? {} : { team: [] }),
          };
        }
        return c;
      });
      setAllCases(updatedCases);
      setFilteredCases(updatedCases);
      setSelectedCases([]);
      setIsSelectionMode(false);
      toast.success(`${pendingCasesToDispose.length} case(s) disposed successfully.`);
    } catch (error) {
      console.error("Error disposing all cases: ", error);
      toast.error("Error disposing cases. Please try again.");
    } finally {
      setIsDisposingAll(false);
    }
  };

  // Build the list of cases to export based on the selected scope.
  // Preserves the order shown on the All Cases listing (newest first by timestamp).
  const getCasesForExport = (scope) => {
    let list = allCases || [];
    if (scope === "selected") {
      const selectedIds = new Set(selectedCasesForPrint.map((c) => c.id));
      list = list.filter((c) => selectedIds.has(c.id));
    } else if (scope === "pending") {
      list = list.filter((c) => getStatusBucket(c.status) === "pending");
    } else if (scope === "disposed") {
      list = list.filter((c) => getStatusBucket(c.status) === "disposed");
    }
    return list;
  };

  const scopeLabel = (scope) =>
    scope === "pending"
      ? "Pending"
      : scope === "disposed"
      ? "Disposed"
      : scope === "selected"
      ? "Selected"
      : "All";

  // Title shown at the top of PDF/Excel exports.
  // For "disposed" and "selected" scopes, the heading reads "All Cases as on ...".
  const exportTitleScope = (scope) =>
    scope === "disposed" || scope === "selected" ? "All" : scopeLabel(scope);

  const addPdfHeader = (doc, title) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(title, 14, 20);
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(14, 25, 196, 25);
    doc.setFontSize(12);
  };

  const formatPartyWithRelation = (name, relation, relationName) => {
    const n = (name || "").trim();
    const r = (relation || "").trim();
    const rn = (relationName || "").trim();
    if (!n) return "";
    if (r && rn) return `${n} ${r} ${rn}`;
    return n;
  };

  // Main petitioner with relation merged inline ("Ajay S/o Abhay")
  const formatPetitionerName = (item) =>
    formatPartyWithRelation(
      item.petitionerName,
      item.petitionerRelation,
      item.petitionerRelationName
    );

  const formatRespondentName = (item) =>
    formatPartyWithRelation(
      item.respondentName,
      item.respondentRelation,
      item.respondentRelationName
    );

  // Combined petitioners (main + others) with each entry's relation merged inline
  const formatAllPetitioners = (item) => {
    const all = Array.isArray(item.allPetitioners) ? item.allPetitioners : [];
    const mainName = (item.petitionerName || "").trim();
    const main = formatPetitionerName(item);
    const others = all
      .filter((p) => (p?.name || "").trim() !== mainName)
      .map((p) => formatPartyWithRelation(p?.name, p?.relation, p?.relationName))
      .filter((s) => s !== "");
    return [main, ...others].filter((s) => s !== "").join(", ");
  };

  const formatAllRespondents = (item) => {
    const all = Array.isArray(item.allRespondents) ? item.allRespondents : [];
    const mainName = (item.respondentName || "").trim();
    const main = formatRespondentName(item);
    const others = all
      .filter((r) => (r?.name || "").trim() !== mainName)
      .map((r) => formatPartyWithRelation(r?.name, r?.relation, r?.relationName))
      .filter((s) => s !== "");
    return [main, ...others].filter((s) => s !== "").join(", ");
  };

  const formatAllPetitionerAdvocates = (item) => {
    const main = (item.petitionerAdvocate || "").trim();
    const all = Array.isArray(item.allPetitionerAdvocates)
      ? item.allPetitionerAdvocates
      : [];
    const others = all
      .map((a) => (a?.name || "").trim())
      .filter((n) => n !== "" && n !== main);
    return [main, ...others].filter((n) => n !== "").join(", ");
  };

  const formatAllRespondentAdvocates = (item) => {
    const main = (item.respondentAdvocate || "").trim();
    const all = Array.isArray(item.allRespondentAdvocates)
      ? item.allRespondentAdvocates
      : [];
    const others = all
      .map((a) => (a?.name || "").trim())
      .filter((n) => n !== "" && n !== main);
    return [main, ...others].filter((n) => n !== "").join(", ");
  };

  const getFieldValue = (item, key) => {
    switch (key) {
      case "caseNumber":
        return item.caseNumber || "";
      case "type":
        return item.type || "";
      case "caseYear":
        return item.caseYear || "";
      case "status":
        return item.status || "";
      case "court":
        return item.court || "";
      case "caseFiledDate":
        return item.caseFiledDate || "";
      case "partyContactNo":
        return item.partyContactNo || "";
      case "previousHearingDate":
        return item.previousHearingDate || "";
      case "nextHearingDate":
        return item.nextHearingDate || "";
      case "stayOrderDate":
        return item.stayOrderDate || "";
      case "counterAffidavitStatus":
        return item.counterAffidavitStatus || "";
      case "counterAppealFiled":
        return item.counterAppealFiled || "";
      case "authorityDepartment":
        return item.authorityDepartment === "Others"
          ? item.authorityDepartmentOther || ""
          : item.authorityDepartment || "";
      case "village":
        return item.village || "";
      case "area":
        return item.area || "";
      case "properties":
        return item.properties || "";
      case "petitioner":
        return formatAllPetitioners(item);
      case "petitionerAdvocate":
        return formatAllPetitionerAdvocates(item);
      case "respondent":
        return formatAllRespondents(item);
      case "respondentAdvocate":
        return formatAllRespondentAdvocates(item);
      case "prayers":
        return item.prayers || "";
      case "caseNotes":
        return item.caseNotes || "";
      default:
        return "";
    }
  };

  const isFieldKeyAvailable = (key) => {
    const def = PRINT_FIELD_GROUPS.flatMap((g) => g.fields).find(
      (f) => f.key === key
    );
    if (!def) return false;
    if (def.deptOnly && !isDepartmentVerified) return false;
    return true;
  };

  const togglePrintField = (key) => {
    setSelectedPrintFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setAllPrintFields = (selectAll) => {
    if (selectAll) {
      setSelectedPrintFields(
        new Set(ALL_PRINT_FIELD_KEYS.filter(isFieldKeyAvailable))
      );
    } else {
      setSelectedPrintFields(new Set());
    }
  };

  const getActiveSelectedFieldKeys = () =>
    ALL_PRINT_FIELD_KEYS.filter(
      (k) => selectedPrintFields.has(k) && isFieldKeyAvailable(k)
    );

  const exportPrintPdf = () => {
    const data = getCasesForExport(printScope);
    if (!data.length) {
      toast.error("No cases to export.");
      return;
    }

    const activeKeys = getActiveSelectedFieldKeys();
    if (!activeKeys.length) {
      toast.error("Select at least one field to print.");
      return;
    }

    try {
      const doc = new jsPDF();
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, "0");
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const yyyy = today.getFullYear();
      const asOnDate = `${dd}-${mm}-${yyyy}`;
      const headerTitle = `${exportTitleScope(printScope)} Cases as on ${asOnDate}`;
      addPdfHeader(doc, headerTitle);

      const config = {
        leftMargin: 15,
        topPadding: 10,
        bottomPadding: 10,
        boxWidth: 180,
        threeColGap: 6,
        twoColGap: 12,
        lineHeight: 5,
        fieldSpacing: 4,
        sectionSpacing: 5,
        spaceBetweenBoxes: 8,
      };
      const fullWidth = config.boxWidth - 4;
      const threeColWidth = (config.boxWidth - 2 * config.threeColGap) / 3;
      const twoColWidth = (config.boxWidth - config.twoColGap) / 2;

      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 45;

      const fieldLabel = (key) => {
        const def = PRINT_FIELD_GROUPS.flatMap((g) => g.fields).find(
          (f) => f.key === key
        );
        return def ? `${def.label.toUpperCase()}:` : "";
      };

      const drawField = (label, value, x, y, width) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        const labelLines = doc.splitTextToSize(label, width);
        doc.text(labelLines, x, y);
        let cursor =
          y +
          (doc.getTextDimensions(labelLines).h ||
            labelLines.length * config.lineHeight);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        const valueLines = doc.splitTextToSize(String(value || "N/A"), width);
        doc.text(valueLines, x, cursor);
        cursor +=
          doc.getTextDimensions(valueLines).h ||
          valueLines.length * config.lineHeight;
        return cursor;
      };

      const measureField = (label, value, width) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        const labelH = doc.getTextDimensions(
          doc.splitTextToSize(label, width)
        ).h;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        const valueH = doc.getTextDimensions(
          doc.splitTextToSize(String(value || "N/A"), width)
        ).h;
        return labelH + valueH;
      };

      const measureColumn = (entries, width) =>
        entries.reduce((acc, [label, value], i) => {
          const h = measureField(label, value, width);
          return acc + h + (i < entries.length - 1 ? config.fieldSpacing : 0);
        }, 0);

      const drawColumn = (entries, xPos, startY, width) => {
        let cursor = startY;
        entries.forEach(([label, value], i) => {
          cursor = drawField(label, value, xPos, cursor, width);
          if (i < entries.length - 1) cursor += config.fieldSpacing;
        });
        return cursor;
      };

      const distributeBalanced = (entries, columns, width) => {
        const buckets = Array.from({ length: columns }, () => []);
        const heights = new Array(columns).fill(0);
        entries.forEach((entry) => {
          let target = 0;
          for (let i = 1; i < columns; i++) {
            if (heights[i] < heights[target]) target = i;
          }
          const h = measureField(entry[0], entry[1], width);
          buckets[target].push(entry);
          heights[target] += h + config.fieldSpacing;
        });
        return buckets;
      };

      const generalKeys = PRINT_FIELD_GROUPS[0].fields
        .map((f) => f.key)
        .filter((k) => activeKeys.includes(k));
      const partyKeys = PRINT_FIELD_GROUPS[1].fields
        .map((f) => f.key)
        .filter((k) => activeKeys.includes(k));
      const longKeys = PRINT_FIELD_GROUPS[2].fields
        .map((f) => f.key)
        .filter((k) => activeKeys.includes(k));

      const petKeyOrder = ["petitioner", "petitionerAdvocate"];
      const resKeyOrder = ["respondent", "respondentAdvocate"];

      data.forEach((item, index) => {
        const generalEntries = generalKeys.map((k) => [
          fieldLabel(k),
          getFieldValue(item, k),
        ]);
        const petitionerEntries = petKeyOrder
          .filter((k) => partyKeys.includes(k))
          .map((k) => [fieldLabel(k), getFieldValue(item, k)]);
        const respondentEntries = resKeyOrder
          .filter((k) => partyKeys.includes(k))
          .map((k) => [fieldLabel(k), getFieldValue(item, k)]);
        const longEntries = longKeys.map((k) => [
          fieldLabel(k),
          getFieldValue(item, k),
        ]);

        const generalBuckets = distributeBalanced(
          generalEntries,
          3,
          threeColWidth
        );
        const generalHeight =
          generalEntries.length > 0
            ? Math.max(
                ...generalBuckets.map((b) =>
                  b.length ? measureColumn(b, threeColWidth) : 0
                )
              )
            : 0;

        const partiesHeight =
          petitionerEntries.length || respondentEntries.length
            ? Math.max(
                petitionerEntries.length
                  ? measureColumn(petitionerEntries, twoColWidth)
                  : 0,
                respondentEntries.length
                  ? measureColumn(respondentEntries, twoColWidth)
                  : 0
              )
            : 0;

        const longHeights = longEntries.map(([label, value]) =>
          measureField(label, value, fullWidth)
        );

        let boxHeight = config.topPadding + config.bottomPadding;
        if (generalHeight) boxHeight += generalHeight;
        if (partiesHeight) {
          if (generalHeight) boxHeight += config.sectionSpacing;
          boxHeight += partiesHeight;
        }
        longHeights.forEach((h, i) => {
          if (i === 0 && (generalHeight || partiesHeight))
            boxHeight += config.sectionSpacing;
          else if (i > 0) boxHeight += config.sectionSpacing;
          boxHeight += h;
        });

        const spaceNeeded =
          boxHeight + (index < data.length - 1 ? config.spaceBetweenBoxes : 0);

        if (yPosition + spaceNeeded > pageHeight - 20) {
          doc.addPage();
          addPdfHeader(doc, headerTitle);
          yPosition = 45;
        }

        doc.rect(
          config.leftMargin - 2,
          yPosition - config.topPadding,
          config.boxWidth,
          boxHeight
        );

        let cursor = yPosition;

        if (generalHeight) {
          const xs = [
            config.leftMargin,
            config.leftMargin + threeColWidth + config.threeColGap,
            config.leftMargin + (threeColWidth + config.threeColGap) * 2,
          ];
          generalBuckets.forEach((bucket, i) => {
            if (bucket.length) drawColumn(bucket, xs[i], cursor, threeColWidth);
          });
          cursor += generalHeight;
        }

        if (partiesHeight) {
          if (generalHeight) cursor += config.sectionSpacing;
          if (petitionerEntries.length)
            drawColumn(petitionerEntries, config.leftMargin, cursor, twoColWidth);
          if (respondentEntries.length)
            drawColumn(
              respondentEntries,
              config.leftMargin + twoColWidth + config.twoColGap,
              cursor,
              twoColWidth
            );
          cursor += partiesHeight;
        }

        longEntries.forEach(([label, value], i) => {
          if (i === 0 && (generalHeight || partiesHeight))
            cursor += config.sectionSpacing;
          else if (i > 0) cursor += config.sectionSpacing;
          cursor = drawField(label, value, config.leftMargin, cursor, fullWidth);
        });

        yPosition += boxHeight + config.spaceBetweenBoxes;
      });

      const fileName = `${scopeLabel(printScope)}_Cases_${Date.now()}.pdf`;
      doc.save(fileName);
      toast.success("Cases exported successfully.");
      finishPrintExport();
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export cases to PDF.");
    }
  };

  const finishPrintExport = () => {
    setShowPrintModal(false);
    if (printScope === "selected") {
      setIsPrintSelectionMode(false);
      setSelectedCasesForPrint([]);
      setPrintScope("pending");
    }
  };

  const exportPrintExcel = () => {
    const data = getCasesForExport(printScope);
    if (!data.length) {
      toast.error("No cases to export.");
      return;
    }

    const activeKeys = getActiveSelectedFieldKeys();
    if (!activeKeys.length) {
      toast.error("Select at least one field to export.");
      return;
    }

    try {
      const escapeHtml = (val) => {
        if (val === null || val === undefined) return "";
        return String(val)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
      };

      const today = new Date();
      const dd = String(today.getDate()).padStart(2, "0");
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const yyyy = today.getFullYear();
      const asOnDate = `${dd}-${mm}-${yyyy}`;

      const titleText = `${exportTitleScope(printScope)} Cases as on ${asOnDate}`;

      const fieldDefByKey = Object.fromEntries(
        PRINT_FIELD_GROUPS.flatMap((g) => g.fields).map((f) => [f.key, f])
      );

      const headers = ["S.N.", ...activeKeys.map((k) => fieldDefByKey[k].label)];

      const rows = data.map((item, idx) => [
        idx + 1,
        ...activeKeys.map((k) => getFieldValue(item, k)),
      ]);

      const headerCells = headers
        .map(
          (h) =>
            `<th style="border:1px solid #000;padding:6px;background:#e7e6e6;font-weight:bold;text-align:center;">${escapeHtml(
              h
            )}</th>`
        )
        .join("");

      const bodyRows = rows
        .map(
          (row) =>
            `<tr>${row
              .map(
                (cell) =>
                  `<td style="border:1px solid #000;padding:6px;vertical-align:top;">${escapeHtml(
                    cell
                  )}</td>`
              )
              .join("")}</tr>`
        )
        .join("");

      const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="UTF-8" /><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>${scopeLabel(
        printScope
      )} Cases</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body><table border="1" style="border-collapse:collapse;font-family:Arial, sans-serif;font-size:11px;"><tr><th colspan="${headers.length}" style="border:1px solid #000;padding:8px;text-align:center;font-weight:bold;font-size:14px;">${escapeHtml(
        titleText
      )}</th></tr><tr>${headerCells}</tr>${bodyRows}</table></body></html>`;

      const blob = new Blob([html], { type: "application/vnd.ms-excel" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${scopeLabel(printScope)}_Cases_${Date.now()}.xls`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("Cases exported to Excel successfully.");
      finishPrintExport();
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Failed to export cases to Excel.");
    }
  };

  const handlePrintExport = () => {
    if (printFormat === "pdf") exportPrintPdf();
    else exportPrintExcel();
  };

  let searchPlaceholder = isDepartmentVerified
    ? "Search by case number, year, court, petitioner, respondent, advocate, case type, department, village/division, khasra/plot, area..."
    : "Search by case number, year, filing number, court, petitioner, respondent, petitioner advocate, respondent advocate, case type...";
  if (searchField === "authorityDepartment") {
    searchPlaceholder = selectedAuthorityDepartments.length > 0
      ? "Search village, properties, or area in selected departments..."
      : "Type department, properties, area, or village...";
  } else if (searchField === "counterAffidavitStatus") {
    searchPlaceholder = "Type counter affidavit status...";
  } else if (searchField === "caseNumber") {
    searchPlaceholder = "Type case number...";
  }

  // Any of the bulk-selection modes → the table shows a checkbox column and
  // clicking a row toggles selection instead of navigating.
  const selectionActive =
    isSelectionMode ||
    isShareSelectionMode ||
    isPrintSelectionMode ||
    isLabelAssignmentMode;

  const isCaseLocked = (c) =>
    (c.collection === "pending" && lockedCasesMap.get(c.id)?.isLocked) || false;

  const toggleInSet = (list, setList, entry) => {
    const exists = list.some((x) => x.id === entry.id);
    setList(exists ? list.filter((x) => x.id !== entry.id) : [...list, entry]);
  };

  // Row click behavior: toggle selection in a selection mode, open the upgrade
  // modal for a locked case, else navigate to the case detail page.
  const handleRowActivate = (caseItem) => {
    const entry = { id: caseItem.id, collection: caseItem.collection };
    if (isShareSelectionMode) {
      if (!caseItem.isShared)
        toggleInSet(selectedCasesForShare, setSelectedCasesForShare, entry);
      return;
    }
    if (isSelectionMode) {
      if (!caseItem.isShared)
        toggleInSet(selectedCases, setSelectedCases, entry);
      return;
    }
    if (isLabelAssignmentMode) {
      toggleInSet(selectedCasesForLabel, setSelectedCasesForLabel, caseItem);
      return;
    }
    if (isPrintSelectionMode) {
      toggleInSet(selectedCasesForPrint, setSelectedCasesForPrint, caseItem);
      return;
    }
    if (isCaseLocked(caseItem)) {
      setLockedCaseClicked(caseItem);
      setShowUpgradeModal(true);
      return;
    }
    clearNewOrdersBadge(caseItem);
    navigate(
      `/portal/case/${caseItem.id}${caseItem.isShared ? "?from=shared" : ""}`
    );
  };

  // The new-order counter is an "unseen" marker, so opening the case clears it.
  // Local state is updated first so the badge disappears immediately even if the
  // Firestore write is slow or rejected (shared users have no write access on
  // the owner's case document).
  const clearNewOrdersBadge = async (caseItem) => {
    if (!caseItem?.newOrdersCount) return;

    const reset = (list) =>
      list.map((c) => (c.id === caseItem.id ? { ...c, newOrdersCount: 0 } : c));
    // visibleCases derives from filteredCases, so both have to be reset.
    setAllCases(reset);
    setFilteredCases(reset);

    try {
      await updateDoc(
        doc(db, caseItem.collection === "disposed" ? "disposed" : "pending", caseItem.id),
        { newOrdersCount: 0 }
      );
    } catch (error) {
      console.error("Could not clear new-order badge:", error);
    }
  };

  return (
    <div className="crm-compact min-h-screen relative bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="relative">
          {/* Page header: title + subtitle (left), power-actions (right).
              Sidebar + topbar are provided by PortalShell — no in-page nav. */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                Cases
              </h1>
              <p className="mt-0.5 text-xs text-slate-500">
                Manage all your cases and track progress
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:justify-end sm:gap-3">
            {isPrintSelectionMode ? (
              <>
                {selectedCasesForPrint.length > 0 && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full">
                    <Printer className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm font-medium text-slate-700">
                      {selectedCasesForPrint.length} selected
                    </span>
                  </div>
                )}
                <button
                  onClick={() => {
                    if (selectedCasesForPrint.length > 0) {
                      setPrintScope("selected");
                    } else {
                      setPrintScope((prev) => (prev === "selected" ? "pending" : prev));
                    }
                    setShowPrintModal(true);
                  }}
                  className="px-3 py-1.5 rounded-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium transition-colors duration-150 flex items-center gap-1"
                  title={selectedCasesForPrint.length > 0 ? "Print Selected Cases" : "Print All Cases"}
                >
                  <Printer className="h-4 w-4" />
                  {selectedCasesForPrint.length > 0 ? "Print Selected" : "Print All"}
                </button>
                <button
                  onClick={() => {
                    setIsPrintSelectionMode(false);
                    setSelectedCasesForPrint([]);
                  }}
                  className="p-2 rounded-full hover:bg-slate-100 text-yellow-600 transition-colors duration-150"
                  title="Cancel Print Selection"
                >
                  <X className="h-5 w-5" />
                </button>
              </>
            ) : isLabelAssignmentMode ? (
              <>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: selectedLabelForBulkAssign?.color }}
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Assigning: {selectedLabelForBulkAssign?.name}
                  </span>
                </div>
                <button
                  onClick={handleBulkLabelAssign}
                  className="p-2 rounded-full hover:bg-slate-100 text-green-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedCasesForLabel.length === 0}
                  title="Apply Label to Selected Cases"
                >
                  <Check className="h-5 w-5" />
                </button>
                <button
                  onClick={handleCancelBulkAssign}
                  className="p-2 rounded-full hover:bg-slate-100 text-yellow-600 transition-colors duration-150"
                  title="Cancel Label Assignment"
                >
                  <X className="h-5 w-5" />
                </button>
              </>
            ) : isShareSelectionMode ? (
              <>
                {selectedCasesForShare.length > 0 && (
                  <div
  className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
    isLightMode
      ? "bg-gray-200 text-gray-800"
      : "bg-[#2d2a5a] text-white"
  }`}
>
                    <Share2 className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm font-medium text-slate-700">
                      {selectedCasesForShare.length} selected
                    </span>
                  </div>
                )}
                <button
                  onClick={() => setShowShareModal(true)}
                  className="px-3 py-1.5 rounded-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium transition-colors duration-150 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedCasesForShare.length === 0}
                  title="Share Selected Cases"
                >
                  <Share2 className="h-4 w-4" />
                  Share Selected
                </button>
                <button
                  onClick={() => {
                    setIsShareSelectionMode(false);
                    setSelectedCasesForShare([]);
                  }}
                  className="p-2 rounded-full hover:bg-slate-100 text-yellow-600 transition-colors duration-150"
                  title="Cancel Share Selection"
                >
                  <X className="h-5 w-5" />
                </button>
              </>
            ) : isSelectionMode ? (
              <>
                <button
                  onClick={() => setShowConfirmation(true)}
                  className="p-2 rounded-full hover:bg-slate-100 text-red-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedCases.length === 0}
                  title="Delete Selected"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setShowDeleteAllConfirmation(true)}
                  className="light-button-text px-3 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors duration-150"
                  title="Delete All Cases"
                >
                  Delete All
                </button>
                <button
                  onClick={() => {
                    setIsSelectionMode(false);
                    setSelectedCases([]);
                  }}
                  className="p-2 rounded-full hover:bg-slate-100 text-yellow-600 transition-colors duration-150"
                  title="Cancel Selection"
                >
                  <X className="h-5 w-5" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowLabelManager(true)}
                  className="px-3 py-1.5 rounded-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium transition-colors duration-150"
                  title="Create Category"
                >
                  Add Category
                </button>
                <button
                  onClick={() => {
                    setIsShareSelectionMode(true);
                    setSelectedCasesForShare([]);
                  }}
                  className="p-2 rounded-full hover:bg-slate-100 text-green-600 transition-colors duration-150"
                  title="Share Cases"
                >
                  <Share2 className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setShowNotificationModal(true)}
                  className={`p-2 rounded-full hover:bg-slate-100 transition-colors duration-150 ${
                    orderNotificationsEnabled ? "text-yellow-600" : "text-red-600"
                  }`}
                  title={
                    orderNotificationsEnabled
                      ? "Order notifications: On — click to turn off for all cases"
                      : "Order notifications: Off — click to turn back on"
                  }
                >
                  <BellOff className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setIsSelectionMode(true)}
                  className="p-2 rounded-full hover:bg-slate-100 text-slate-500 transition-colors duration-150"
                  title="Enable Delete Mode"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setShowDisposeAllConfirmation(true)}
                  className="p-2 rounded-full hover:bg-slate-100 text-slate-600 transition-colors duration-150"
                  title="Dispose All Pending Cases"
                >
                  <Archive className="h-5 w-5" />
                </button>
              </>
            )}

            {!isPrintSelectionMode && !isLabelAssignmentMode && !isSelectionMode && !isShareSelectionMode && (
              <button
                onClick={() => {
                  setIsPrintSelectionMode(true);
                  setSelectedCasesForPrint([]);
                }}
                className="flex items-center text-slate-600 p-2 rounded-full hover:bg-slate-100 transition-colors duration-150"
                title="Print / Export Cases"
              >
                <Printer className="h-5 w-5" />
              </button>
            )}

            {/* Filters moved down next to the search box — it opened the same
                panel as the "More Filters" button, so the two were duplicates.
                The Tag (assign labels) button moved down there too, beside it. */}
            </div>
          </div>

          {/* ===== Stat cards ===== */}
          <CaseStatCards
            className="mt-4"
            cards={[
              { label: "Total Cases", value: statCounts.total, hint: "View all", Icon: Layers, tint: "blue", onClick: clearAllFilters },
              { label: "Pending", value: statCounts.pending, hint: "View pending", Icon: Clock, tint: "amber", onClick: () => setStatusFilter("pending") },
              { label: "Today's Hearings", value: statCounts.today, hint: "View calendar", Icon: CalendarDays, tint: "green", onClick: () => { setStatusFilter("all"); setHearingDateFilter(todayISO); } },
              { label: "In Progress", value: statCounts.inProgress, hint: "View progress", Icon: Activity, tint: "sky", onClick: () => setStatusFilter("pending") },
              { label: "Closed", value: statCounts.closed, hint: "View closed", Icon: CheckCircle2, tint: "purple", onClick: () => setStatusFilter("disposed") },
            ]}
          />

          {/* ===== Filter / toolbar (single row: search + chips, view toggle right) ===== */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-56">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                id="search-input"
                type="text"
                placeholder="Search cases..."
                title={searchPlaceholder}
                className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {/* The single filter entry point — sits beside search and opens the
                same panel the old top-row icon and "More Filters" button did. */}
            <button
              ref={filterBtnRef}
              type="button"
              onClick={() => setShowFilterOptions((p) => !p)}
              title="Filters"
              aria-label="Filters"
              aria-expanded={showFilterOptions}
              className={`inline-flex shrink-0 items-center justify-center rounded-lg border p-1.5 text-blue-600 shadow-sm transition-colors hover:bg-blue-50 ${
                showFilterOptions
                  ? "border-blue-300 bg-blue-50"
                  : "border-slate-200 bg-white"
              }`}
            >
              <Filter className="h-4 w-4" />
            </button>
            {/* Assign-labels lives beside Filters — same blue as the filter icon. */}
            <button
              type="button"
              onClick={() => setShowLabelList((prev) => !prev)}
              title="Assign Labels to Cases"
              aria-label="Assign Labels to Cases"
              aria-expanded={showLabelList}
              className="inline-flex shrink-0 items-center justify-center rounded-lg p-1.5 text-blue-600 transition-colors hover:bg-blue-50"
            >
              <Tag className="h-4 w-4" />
            </button>
            <ChipSelect
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Status"
              options={[{ value: "pending", label: "Pending" }, { value: "disposed", label: "Disposed" }]}
              ariaLabel="Filter by status"
            />
            <ChipSelect
              value={courtFilter}
              onChange={setCourtFilter}
              placeholder="Court"
              options={courtOptions}
              ariaLabel="Filter by court"
            />
            <ChipSelect
              value={caseTypeFilter}
              onChange={setCaseTypeFilter}
              placeholder="Case Type"
              options={caseTypeOptions}
              ariaLabel="Filter by case type"
            />
            <ChipSelect
              value={advocateFilter}
              onChange={setAdvocateFilter}
              placeholder="Advocate"
              options={advocateOptions}
              ariaLabel="Filter by advocate"
            />
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-800"
              >
                Clear
              </button>
            )}

            {/* View toggle — pushed to the right end of the same row */}
            <div className="ml-auto inline-flex shrink-0 items-center gap-0.5 rounded-lg bg-slate-100 p-0.5">
              {[
                { key: "table", label: "Table", Icon: List },
                { key: "board", label: "Board", Icon: LayoutGrid },
              ].map(({ key, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setViewMode(key)}
                  title={`${label} view`}
                  className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition-all ${
                    viewMode === key
                      ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {showFilterOptions && filterPanelPos &&
            // Anchored to the Filters button and portalled to <body>: it used to
            // be pinned to the viewport's top-right, which left it floating far
            // from its trigger now that the trigger sits beside the search box.
            createPortal(
            <div
              style={{
                left: filterPanelPos.left,
                top: filterPanelPos.top,
                bottom: filterPanelPos.bottom,
                width: filterPanelPos.width,
                maxHeight: filterPanelPos.maxHeight,
              }}
              className={`fixed z-[1000] rounded-lg pt-4 px-4 pb-6 shadow-2xl overflow-y-auto overscroll-contain ${
              isLightMode
              ? "bg-white text-gray-700 border border-gray-300"
              : "bg-[#1e1b4b] text-gray-300 border border-gray-700"
              }`}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-yellow-500">Filter Options</h3>
                <button onClick={() => setShowFilterOptions(false)} className="text-slate-400 hover:text-slate-700">
                  <X size={20} />
                </button>
              </div>

              <div className="mb-3 border-b border-slate-200 pb-2">
                <button
                  type="button"
                  onClick={() => toggleFilterSection("hearingDate")}
                  className="w-full flex justify-between items-center text-sm font-medium text-slate-600 mb-2 hover:text-slate-900"
                >
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    Next Hearing Date
                  </span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${expandedFilterSections.hearingDate ? "rotate-180" : ""}`}
                  />
                </button>
                {expandedFilterSections.hearingDate && (
                  <div className="flex flex-col space-y-1">
                    <input
                      type="date"
                      value={hearingDateFilter}
                      onChange={(e) => setHearingDateFilter(e.target.value)}
                      className="w-full p-2 rounded text-sm bg-white text-slate-700 border border-slate-300 focus:outline-none focus:ring-1 focus:ring-yellow-500 [color-scheme:light]"
                    />
                    {hearingDateFilter && (
                      <button
                        className="filter-option w-full text-left p-2 rounded text-sm text-red-600 hover:bg-red-50"
                        onClick={() => setHearingDateFilter("")}
                      >
                        Clear Date Filter
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-3 border-b border-slate-200 pb-2">
                <button
                  type="button"
                  onClick={() => toggleFilterSection("caseYear")}
                  className="w-full flex justify-between items-center text-sm font-medium text-slate-600 mb-2 hover:text-slate-900"
                >
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    Case Year
                  </span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${expandedFilterSections.caseYear ? "rotate-180" : ""}`}
                  />
                </button>
                {expandedFilterSections.caseYear && (
                  <div className="flex flex-col space-y-1">
                    <DatePicker
                      selected={caseYearFilter ? new Date(caseYearFilter, 0, 1) : null}
                      onChange={(date) => {
                        const year = date ? date.getFullYear().toString() : "";
                        setCaseYearFilter(year);
                      }}
                      showYearPicker
                      dateFormat="yyyy"
                      placeholderText="Select case year"
                      yearItemNumber={9}
                      className="w-full p-2 rounded text-sm bg-white text-slate-700 border border-slate-300 focus:outline-none focus:ring-1 focus:ring-yellow-500"
                      popperPlacement="bottom-start"
                    />
                    {caseYearFilter && (
                      <button
                        className="filter-option w-full text-left p-2 rounded text-sm text-red-600 hover:bg-red-50"
                        onClick={() => setCaseYearFilter("")}
                      >
                        Clear Year Filter
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-3 border-b border-slate-200 pb-2">
                <button
                  type="button"
                  onClick={() => toggleFilterSection("status")}
                  className="w-full flex justify-between items-center text-sm font-medium text-slate-600 mb-2 hover:text-slate-900"
                >
                  <span>Filter By Status</span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${expandedFilterSections.status ? "rotate-180" : ""}`}
                  />
                </button>
                {expandedFilterSections.status && (
                  <div className="flex flex-col space-y-1">
                    {["all", "pending", "disposed"].map((status) => (
                      <button
                        key={status}
                        className={`filter-option w-full text-left p-2 rounded text-sm capitalize ${
                          statusFilter === status.toLowerCase()
                            ? "bg-yellow-500 text-gray-900 hover:bg-yellow-600"
                            : "text-slate-700 hover:bg-slate-100"
                        }`}
                        onClick={() => {
                          setStatusFilter(status.toLowerCase());
                          setShowFilterOptions(false);
                        }}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="mb-3 border-b border-slate-200 pb-2">
                <button
                  type="button"
                  onClick={() => toggleFilterSection("label")}
                  className="w-full flex justify-between items-center text-sm font-medium text-slate-600 mb-2 hover:text-slate-900"
                >
                  <span>Filter By Label</span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${expandedFilterSections.label ? "rotate-180" : ""}`}
                  />
                </button>
                {expandedFilterSections.label && (
                  <div className="flex flex-col space-y-1">
                    {labels.map((label) => (
                      <button
                        key={label.id}
                        className={`filter-option w-full text-left p-2 rounded text-sm flex items-center gap-2 ${
                          labelFilter === label.id
                            ? "bg-yellow-500 text-gray-900 hover:bg-yellow-600"
                            : "text-slate-700 hover:bg-slate-100"
                        }`}
                        onClick={() => {
                          setLabelFilter(label.id);
                          setShowFilterOptions(false);
                        }}
                      >
                        <div
                          className="w-3 h-3 rounded flex-shrink-0"
                          style={{ backgroundColor: label.color }}
                        />
                        {label.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="mb-3 border-b border-slate-200 pb-2">
                <button
                  type="button"
                  onClick={() => toggleFilterSection("stayOrder")}
                  className="w-full flex justify-between items-center text-sm font-medium text-slate-600 mb-2 hover:text-slate-900"
                >
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    Stay Order Date
                  </span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${expandedFilterSections.stayOrder ? "rotate-180" : ""}`}
                  />
                </button>
                {expandedFilterSections.stayOrder && (
                  <div className="flex flex-col space-y-1">
                    <DatePicker
                      selected={stayOrderDateFilter}
                      onChange={(date) => setStayOrderDateFilter(date)}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Select stay order date"
                      className="w-full p-2 rounded text-sm bg-white text-slate-700 border border-slate-300 focus:outline-none focus:ring-1 focus:ring-yellow-500"
                      popperPlacement="bottom-start"
                    />
                    {stayOrderDateFilter && (
                      <button
                        className="filter-option w-full text-left p-2 rounded text-sm text-red-600 hover:bg-red-50"
                        onClick={() => setStayOrderDateFilter(null)}
                      >
                        Clear Stay Order Date
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <button
                  type="button"
                  onClick={() => toggleFilterSection("more")}
                  className="w-full flex justify-between items-center text-sm font-medium text-slate-600 mb-2 hover:text-slate-900"
                >
                  <span>More Filters</span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${expandedFilterSections.more ? "rotate-180" : ""}`}
                  />
                </button>
                {expandedFilterSections.more && (
                  <div className="flex flex-col space-y-1">
                    <button
                      className={`filter-option w-full text-left p-2 rounded text-sm ${
                        disposedWithDirectionFilter === "yes"
                          ? "bg-yellow-500 text-gray-900 hover:bg-yellow-600"
                          : "text-slate-700 hover:bg-slate-100"
                      }`}
                      onClick={() => {
                        const turningOn = disposedWithDirectionFilter !== "yes";
                        setDisposedWithDirectionFilter(turningOn ? "yes" : "all");
                        if (turningOn) setSearchField("all");
                        setShowFilterOptions(false);
                      }}
                    >
                      Disposed with Direction
                    </button>
                    {isDepartmentVerified && (
                      <button
                        className={`filter-option w-full text-left p-2 rounded text-sm flex items-center justify-between ${
                          searchField === "authorityDepartment" || selectedAuthorityDepartments.length > 0
                            ? "bg-yellow-500 text-gray-900 hover:bg-yellow-600"
                            : "text-slate-700 hover:bg-slate-100"
                        }`}
                        onClick={() => {
                          setTempAuthorityDepartments([...selectedAuthorityDepartments]);
                          setShowAuthorityDeptModal(true);
                          setShowFilterOptions(false);
                        }}
                      >
                        <span>Authority Department</span>
                        {selectedAuthorityDepartments.length > 0 && (
                          <span className="ml-2 inline-flex items-center justify-center text-[10px] font-semibold bg-gray-900 text-white rounded-full px-2 py-0.5">
                            {selectedAuthorityDepartments.length}
                          </span>
                        )}
                      </button>
                    )}
                    <button
                      className={`filter-option w-full text-left p-2 rounded text-sm ${
                        searchField === "counterAffidavitStatus"
                          ? "bg-yellow-500 text-gray-900 hover:bg-yellow-600"
                          : "text-slate-700 hover:bg-slate-100"
                      }`}
                      onClick={() => {
                        const turningOn = searchField !== "counterAffidavitStatus";
                        setSearchField(turningOn ? "counterAffidavitStatus" : "all");
                        if (turningOn) setDisposedWithDirectionFilter("all");
                        setShowFilterOptions(false);
                      }}
                    >
                      Status of Counter Affidavit
                    </button>
                  </div>
                )}
              </div>

            </div>,
            document.body
          )}

          {/* Label List for Bulk Assignment */}
          <LabelList
            isOpen={showLabelList}
            onClose={() => setShowLabelList(false)}
            labels={labels}
            onLabelSelect={handleLabelSelectForBulk}
          />
        </div>

        <div className="mt-4">
          {selectedAuthorityDepartments.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500">Filtering by department:</span>
              {selectedAuthorityDepartments.map((dept) => (
                <span
                  key={dept}
                  className="inline-flex items-center gap-1 text-xs bg-yellow-500/15 text-yellow-300 border border-yellow-500/40 rounded-full px-2.5 py-1"
                >
                  {dept}
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedAuthorityDepartments((prev) => {
                        const next = prev.filter((d) => d !== dept);
                        if (next.length === 0) setSearchField("all");
                        return next;
                      })
                    }
                    className="hover:text-white"
                    aria-label={`Remove ${dept}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              <button
                type="button"
                onClick={() => {
                  setSelectedAuthorityDepartments([]);
                  setSearchField("all");
                }}
                className="text-xs text-red-400 hover:text-red-300 underline-offset-2 hover:underline"
              >
                Clear all
              </button>
            </div>
          )}
          {/* Skeleton Loading State */}
          {(isAuthLoading || isLoadingCases) && (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 border-b border-slate-100 px-4 py-4 last:border-0"
                >
                  <div className="h-4 w-20 animate-pulse rounded bg-slate-100" />
                  <div className="h-4 flex-1 animate-pulse rounded bg-slate-100" />
                  <div className="hidden h-4 w-28 animate-pulse rounded bg-slate-100 sm:block" />
                  <div className="h-5 w-16 animate-pulse rounded-full bg-slate-100" />
                  <div className="h-8 w-8 animate-pulse rounded-full bg-slate-100" />
                </div>
              ))}
            </div>
          )}

          {/* Not authenticated - only show after auth loading is complete */}
          {!isAuthLoading && !isLoadingCases && !isUserAuthenticated && (
             <div className="py-16 text-center text-slate-500">
               <p className="text-xl">Please log in to view your cases.</p>
             </div>
          )}

          {/* No results from filter, but cases exist */}
          {!isAuthLoading && !isLoadingCases && visibleCases.length === 0 && isUserAuthenticated && allCases.length > 0 && (
            <div className="py-16 text-center text-slate-500">
              <Search size={48} className="mx-auto mb-4 opacity-40" />
              <p className="text-xl">No cases match your current filters.</p>
              <p>Try adjusting your search or filter criteria.</p>
            </div>
          )}

          {/* No cases at all for the user */}
          {!isAuthLoading && !isLoadingCases && visibleCases.length === 0 && isUserAuthenticated && allCases.length === 0 && (
            <div className="py-16 text-center text-slate-500">
              <Scale size={48} className="mx-auto mb-4 opacity-40" />
              <p className="text-xl">You don't have any cases yet.</p>
              <p>Add cases to see them listed here.</p>
            </div>
          )}

          {/* Cases list — Table + Board views */}
          {!isAuthLoading && !isLoadingCases && visibleCases.length > 0 && (
            viewMode === "board" ? (
              // Board view — same card as the Shared Cases page, wired to this
              // page's data + bulk-selection modes.
              <>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {visibleCases
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map((caseItem) => {
                      const isDisposed = getStatusBucket(caseItem.status) === "disposed";
                      const userLabelIds = caseItem.labelsByUser?.[globalUser?.uid] || caseItem.labels || [];
                      const caseLabels = userLabelIds.map(getLabelById).filter(Boolean);
                      const locked = isCaseLocked(caseItem);
                      const newOrdersCount = caseItem.newOrdersCount || 0;
                      const selected =
                        (isSelectionMode && selectedCases.some((c) => c.id === caseItem.id)) ||
                        (isShareSelectionMode && selectedCasesForShare.some((c) => c.id === caseItem.id)) ||
                        (isPrintSelectionMode && selectedCasesForPrint.some((c) => c.id === caseItem.id)) ||
                        (isLabelAssignmentMode && selectedCasesForLabel.some((c) => c.id === caseItem.id));
                      const canCheck = !caseItem.isShared || isLabelAssignmentMode || isPrintSelectionMode;
                      const shares = resolveMembers(caseItem).filter((m) => m.uid !== globalUser?.uid);
                      const shownShares = shares.slice(0, 4);
                      const extraShares = shares.length - shownShares.length;
                      return (
                        <div
                          key={caseItem.id}
                          onClick={() => handleRowActivate(caseItem)}
                          className={`group flex cursor-pointer flex-col rounded-xl border bg-white p-3.5 shadow-sm transition-shadow hover:shadow-md ${
                            selected ? "border-yellow-400 ring-2 ring-yellow-400/40" : "border-slate-200"
                          } ${locked ? "opacity-70" : ""}`}
                        >
                          <div className="flex gap-3">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-yellow-50 text-yellow-500">
                              {selectionActive && canCheck ? (
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  readOnly
                                  className="h-4 w-4 accent-yellow-500"
                                />
                              ) : locked ? (
                                <Lock className="h-4 w-4 text-slate-400" />
                              ) : (
                                <Scale className="h-4 w-4" />
                              )}
                            </span>
                            <div className="min-w-0 flex-grow">
                              <div className="flex items-start justify-between gap-2">
                                <p className="flex items-center gap-1.5 text-[11px] text-slate-400">
                                  <span>
                                    Case Number: {caseItem.caseNumber || "—"}
                                    {caseItem.caseYear ? ` / ${caseItem.caseYear}` : ""}
                                  </span>
                                  {newOrdersCount > 0 && (
                                    <span
                                      className="inline-flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-bold leading-none"
                                      // Inline color (not `text-white`): lightMode.css remaps
                                      // .text-white to dark gray with !important.
                                      style={{ color: "#ffffff" }}
                                      title={`${newOrdersCount} new order${newOrdersCount === 1 ? "" : "s"} since you last opened this case`}
                                    >
                                      {newOrdersCount > 99 ? "99+" : newOrdersCount}
                                    </span>
                                  )}
                                </p>
                                <div className="flex shrink-0 items-center gap-1.5">
                                  {caseLabels.slice(0, 1).map((label) => (
                                    <span
                                      key={label.id}
                                      className="rounded px-1.5 py-0.5 text-[9px] font-semibold"
                                      style={{ backgroundColor: label.color, color: "#ffffff" }}
                                      title={label.name}
                                    >
                                      {label.name}
                                    </span>
                                  ))}
                                  {!isDisposed && !selectionActive && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (locked) {
                                          setLockedCaseClicked(caseItem);
                                          setShowUpgradeModal(true);
                                          return;
                                        }
                                        clearNewOrdersBadge(caseItem);
                                        navigate(`/portal/case/${caseItem.id}${caseItem.isShared ? "?from=shared" : ""}`);
                                      }}
                                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600 shadow-sm hover:bg-slate-50"
                                    >
                                      <Calendar className="h-3 w-3" /> Update Next Hearing
                                    </button>
                                  )}
                                </div>
                              </div>
                              <h3 className="truncate text-sm font-bold text-slate-900" title={caseItem.type}>
                                {caseItem.type || "Case"}
                              </h3>
                              <p
                                className="mt-1 line-clamp-2 text-[11px] text-slate-600"
                                title={formatPartyNames(caseItem.petitionerName, caseItem.respondentName, 200)}
                              >
                                <span className="font-medium text-slate-500">Party: </span>
                                {formatPartyNames(caseItem.petitionerName, caseItem.respondentName, 60)}
                              </p>
                              <p className="mt-0.5 truncate text-[11px] text-slate-600" title={caseItem.court || ""}>
                                <span className="font-medium text-slate-500">Court: </span>
                                {caseItem.court || "—"}
                              </p>
                              {!isDisposed && (
                                <p
                                  className={`mt-0.5 text-[11px] font-medium ${
                                    caseItem.nextHearingDate ? "text-blue-600" : "text-orange-500"
                                  }`}
                                >
                                  Next Hearing Date: {caseItem.nextHearingDate ? formatDate(caseItem.nextHearingDate) : "Not Set"}
                                </p>
                              )}
                              <p className={`mt-0.5 text-[11px] font-medium ${isDisposed ? "text-rose-500" : "text-green-600"}`}>
                                Status: {caseItem.status ? caseItem.status.charAt(0).toUpperCase() + caseItem.status.slice(1).toLowerCase() : "—"}
                              </p>

                              {shares.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-[10px] text-slate-400">Shared to:</p>
                                  <div className="mt-1 flex items-center -space-x-1.5">
                                    {shownShares.map((m, i) => (
                                      <span
                                        key={m.uid || i}
                                        title={m.name || m.uid}
                                        className={`flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-semibold ring-2 ring-white ${colorFor(m.name || m.uid)}`}
                                      >
                                        {initialsOf(m.name || m.uid)}
                                      </span>
                                    ))}
                                    {extraShares > 0 && (
                                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-[9px] font-semibold text-slate-600 ring-2 ring-white">
                                        +{extraShares}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalItems={visibleCases.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={(page) => {
                    setCurrentPage(page);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  onItemsPerPageChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                />
              </>
            ) : (
              <>
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1120px] text-left text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50/70 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          {selectionActive && <th className="w-10 px-3 py-2.5" />}
                          <th className="px-3 py-2.5">Case No.</th>
                          <th className="px-3 py-2.5">Case Title</th>
                          <th className="px-3 py-2.5">Client / Party</th>
                          <th className="px-3 py-2.5">Court</th>
                          <th className="px-3 py-2.5">Advocate</th>
                          <th className="px-3 py-2.5">Filed On</th>
                          <th className="px-3 py-2.5">Prev. Hearing</th>
                          <th className="px-3 py-2.5">Next Hearing</th>
                          <th className="px-3 py-2.5">Status</th>
                          <th className="px-3 py-2.5">Category</th>
                          <th className="px-3 py-2.5">Team</th>
                          <th className="px-3 py-2.5 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {visibleCases
                          .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                          .map((caseItem) => {
                            const isDisposed = getStatusBucket(caseItem.status) === "disposed";
                            const showAlert = shouldShowUpdateBanner(caseItem.nextHearingDate, caseItem.status);
                            const userLabelIds = caseItem.labelsByUser?.[globalUser?.uid] || caseItem.labels || [];
                            const caseLabels = userLabelIds.map(getLabelById).filter(Boolean);
                            const locked = isCaseLocked(caseItem);
                            const newOrdersCount = caseItem.newOrdersCount || 0;
                            const rowSelected =
                              (isSelectionMode && selectedCases.some((c) => c.id === caseItem.id)) ||
                              (isShareSelectionMode && selectedCasesForShare.some((c) => c.id === caseItem.id)) ||
                              (isPrintSelectionMode && selectedCasesForPrint.some((c) => c.id === caseItem.id)) ||
                              (isLabelAssignmentMode && selectedCasesForLabel.some((c) => c.id === caseItem.id));
                            const clientParty =
                              caseItem.appearingFor === "respondent"
                                ? caseItem.respondentName || caseItem.petitionerName
                                : caseItem.petitionerName || caseItem.respondentName;
                            const members = resolveMembers(caseItem);
                            const advocate = caseItem.petitionerAdvocate || caseItem.respondentAdvocate || "";
                            const canCheck = !caseItem.isShared || isLabelAssignmentMode || isPrintSelectionMode;
                            return (
                              <tr
                                key={caseItem.id}
                                onClick={() => handleRowActivate(caseItem)}
                                className={`cursor-pointer transition-colors hover:bg-slate-50 ${rowSelected ? "bg-yellow-50" : ""} ${locked ? "opacity-70" : ""}`}
                              >
                                {selectionActive && (
                                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                                    {canCheck && (
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 accent-yellow-500"
                                        checked={rowSelected}
                                        onChange={() => handleRowActivate(caseItem)}
                                      />
                                    )}
                                  </td>
                                )}
                                <td className="px-3 py-2.5 align-top">
                                  <div className="flex items-center gap-1.5 font-semibold text-slate-900">
                                    {locked && <Lock className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                                    <span>
                                      {caseItem.caseNumber || "—"}
                                      {caseItem.caseYear ? ` / ${caseItem.caseYear}` : ""}
                                    </span>
                                    {newOrdersCount > 0 && (
                                      <span
                                        className="inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-red-600 px-1.5 text-[10px] font-bold leading-none"
                                        // Inline color (not `text-white`): lightMode.css remaps
                                        // .text-white to dark gray with !important.
                                        style={{ color: "#ffffff" }}
                                        title={`${newOrdersCount} new order${newOrdersCount === 1 ? "" : "s"} since you last opened this case`}
                                      >
                                        {newOrdersCount > 99 ? "99+" : newOrdersCount}
                                      </span>
                                    )}
                                  </div>
                                  {caseItem.isShared && (
                                    <span className="mt-1 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600 ring-1 ring-inset ring-blue-200">
                                      Shared
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 align-top">
                                  <div
                                    className="max-w-[240px] truncate font-medium text-slate-800"
                                    title={formatPartyNames(caseItem.petitionerName, caseItem.respondentName, 200)}
                                  >
                                    {formatPartyNames(caseItem.petitionerName, caseItem.respondentName, 60)}
                                  </div>
                                  <div className="mt-1 flex flex-wrap items-center gap-1">
                                    {caseItem.type && (
                                      <span className="text-xs text-slate-400">{caseItem.type}</span>
                                    )}
                                    {showAlert && !isDisposed && (
                                      <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
                                        <Info className="h-3 w-3" /> Update hearing
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-2.5 align-top">
                                  <div className="max-w-[180px] truncate text-slate-700" title={clientParty || ""}>
                                    {clientParty || "—"}
                                  </div>
                                </td>
                                <td className="px-3 py-2.5 align-top">
                                  <div className="max-w-[160px] truncate text-slate-700" title={caseItem.court || ""}>
                                    {caseItem.court || "—"}
                                  </div>
                                </td>
                                <td className="px-3 py-2.5 align-top">
                                  <div className="max-w-[150px] truncate text-slate-700" title={advocate}>
                                    {advocate || "—"}
                                  </div>
                                </td>
                                <td className="px-3 py-2.5 align-top whitespace-nowrap text-slate-700">
                                  {caseItem.caseFiledDate || "—"}
                                </td>
                                <td className="px-3 py-2.5 align-top whitespace-nowrap text-slate-700">
                                  {caseItem.previousHearingDate || "—"}
                                </td>
                                <td className="px-3 py-2.5 align-top whitespace-nowrap text-slate-700">
                                  {isDisposed ? (
                                    "—"
                                  ) : caseItem.nextHearingDate ? (
                                    formatDate(caseItem.nextHearingDate)
                                  ) : (
                                    <span className="text-amber-600">Not set</span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 align-top">
                                  <StatusPill status={caseItem.status} />
                                </td>
                                <td className="px-3 py-2.5 align-top">
                                  {caseLabels.length > 0 ? (
                                    <div className="flex max-w-[140px] flex-wrap gap-1">
                                      {caseLabels.slice(0, 3).map((label) => (
                                        <span
                                          key={label.id}
                                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                                          // Inline color (not `text-white`): lightMode.css remaps
                                          // .text-white to dark gray, which made pill text unreadable.
                                          style={{ backgroundColor: label.color, color: "#ffffff" }}
                                          title={label.name}
                                        >
                                          {label.name}
                                        </span>
                                      ))}
                                      {caseLabels.length > 3 && (
                                        <span
                                          className="inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600"
                                          title={caseLabels.slice(3).map((l) => l.name).join(", ")}
                                        >
                                          +{caseLabels.length - 3}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-slate-400">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 align-top">
                                  <TeamAvatarsNishant members={members} />
                                </td>
                                <td className="px-3 py-2.5 align-top text-right" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      clearNewOrdersBadge(caseItem);
                                      navigate(`/portal/case/${caseItem.id}${caseItem.isShared ? "?from=shared" : ""}`);
                                    }}
                                    title="View case details"
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                                  >
                                    <Eye className="h-3.5 w-3.5" /> View
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalItems={visibleCases.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={(page) => {
                    setCurrentPage(page);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  onItemsPerPageChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                />
              </>
            )
          )}
        </div>

        {showConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white text-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm">
              <h3 className="text-xl font-semibold mb-2 text-red-600">Delete Selected Cases</h3>
              <p className="text-sm text-gray-500 mb-4">This action cannot be undone.</p>
              <p className="text-sm mb-6">
                Are you sure you want to delete <span className="font-semibold text-red-600">{selectedCases.length} selected case(s)</span>? This will permanently remove them from your account.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="px-4 py-2 rounded text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 rounded text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteAllConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white text-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm">
              <h3 className="text-xl font-semibold mb-2 text-red-600">Delete All Cases</h3>
              <p className="text-sm text-gray-500 mb-4">This action cannot be undone.</p>
              <p className="text-sm mb-6">
                Are you sure you want to delete <span className="font-semibold text-red-600">all {allCases.length} case(s)</span>? This will permanently remove every pending and disposed case from your account.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteAllConfirmation(false)}
                  className="px-4 py-2 rounded text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAll}
                  className="px-4 py-2 rounded text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {disposeSharingModal}

      {/* Dispose All Confirmation Modal */}
        {showDisposeAllConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="dispose-modal bg-[#0B0A29] text-white p-6 rounded-lg shadow-xl w-full max-w-sm border border-gray-700">
              <h3 className="dispose-modal-title text-xl font-semibold mb-2 text-yellow-500">Dispose All Pending Cases</h3>
              <p className="dispose-modal-text text-sm text-white mb-6">
                Are you sure you want to dispose <span className="font-bold text-white">all {allCases.filter((c) => c.collection === "pending").length} pending case(s)</span>? Their status will be changed to disposed and reminders will be cleared. For shared cases you will be asked whether to keep or revoke sharing.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDisposeAllConfirmation(false)}
                  className="px-4 py-2 rounded text-sm font-medium bg-white text-black hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisposeAll}
                  disabled={isDisposingAll}
                  className="px-4 py-2 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {isDisposingAll && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isDisposingAll ? "Disposing..." : "Dispose All"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Authority Department Multi-Select Modal */}
        {showAuthorityDeptModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[1200] p-4">
            <div className="bg-[#0B0A29] text-white rounded-lg shadow-xl w-full max-w-md border border-gray-700 flex flex-col max-h-[85vh]">
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-700">
                <h3 className="text-lg font-semibold text-yellow-500">Filter by Department</h3>
                <button
                  onClick={() => setShowAuthorityDeptModal(false)}
                  className="text-gray-400 hover:text-white"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-6 py-3 border-b border-gray-700/70">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-yellow-500"
                    checked={tempAuthorityDepartments.length === departmentOptions.length}
                    onChange={(e) => {
                      setTempAuthorityDepartments(
                        e.target.checked ? [...departmentOptions] : []
                      );
                    }}
                  />
                  <span className="font-medium">Select All</span>
                  <span className="ml-auto text-xs text-gray-400">
                    {tempAuthorityDepartments.length}/{departmentOptions.length} selected
                  </span>
                </label>
              </div>

              <div className="px-6 py-3 overflow-y-auto flex-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {departmentOptions.map((dept) => {
                    const checked = tempAuthorityDepartments.includes(dept);
                    return (
                      <label
                        key={dept}
                        className="flex items-center gap-2 px-3 py-2 rounded cursor-pointer hover:bg-[#1e1b4b] text-sm"
                      >
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-yellow-500"
                          checked={checked}
                          onChange={(e) => {
                            setTempAuthorityDepartments((prev) =>
                              e.target.checked
                                ? [...prev, dept]
                                : prev.filter((d) => d !== dept)
                            );
                          }}
                        />
                        <span>{dept}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-between items-center px-6 py-4 border-t border-gray-700">
                <button
                  onClick={() => setTempAuthorityDepartments([])}
                  className="text-sm text-red-400 hover:text-red-300"
                >
                  Clear
                </button>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowAuthorityDeptModal(false)}
                    className="px-4 py-2 rounded text-sm font-medium bg-white text-black hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setSelectedAuthorityDepartments([...tempAuthorityDepartments]);
                      const turningOn = tempAuthorityDepartments.length > 0;
                      setSearchField(turningOn ? "authorityDepartment" : "all");
                      if (turningOn) setDisposedWithDirectionFilter("all");
                      setShowAuthorityDeptModal(false);
                    }}
                    className="px-4 py-2 rounded text-sm font-medium bg-yellow-500 text-black hover:bg-yellow-400 transition-colors"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Print / Export Modal */}
        {showPrintModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-start justify-center z-[1100] px-2 pt-24 pb-4 overflow-y-auto">
            <div className="bg-[#0B0A29] text-white p-3 sm:p-5 rounded-lg shadow-xl w-full max-w-lg border border-gray-700 max-h-[calc(100vh-7rem)] flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-yellow-500">Print / Export Cases</h3>
                <button
                  onClick={() => setShowPrintModal(false)}
                  className="text-gray-400 hover:text-white"
                  title="Close"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="overflow-y-auto pr-1 flex-1">
              <div className="mb-3">
                <p className="text-sm text-gray-300 mb-2 font-medium">Format</p>
                <div className="flex gap-3">
                  {[
                    { v: "pdf", label: "PDF" },
                    { v: "excel", label: "Excel" },
                  ].map((opt) => (
                    <label
                      key={opt.v}
                      className={`flex-1 cursor-pointer px-4 py-2 rounded-lg border text-center text-sm transition-colors ${
                        printFormat === opt.v
                          ? "bg-blue-900/40 border-blue-500 text-blue-200 font-semibold"
                          : "bg-[#1A1535] border-gray-600 text-gray-300 hover:border-blue-500"
                      }`}
                    >
                      <input
                        type="radio"
                        name="printFormat"
                        value={opt.v}
                        checked={printFormat === opt.v}
                        onChange={() => setPrintFormat(opt.v)}
                        className="hidden"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <p className="text-sm text-gray-300 mb-2 font-medium">Cases to include</p>
                {printScope === "selected" ? (
                  <div className="px-4 py-2 rounded-lg border border-blue-500 bg-blue-900/40 text-blue-200 text-sm text-center font-semibold">
                    {selectedCasesForPrint.length} selected case(s)
                  </div>
                ) : (
                  <div className="flex gap-3">
                    {[
                      { v: "pending", label: "Pending" },
                      { v: "disposed", label: "Disposed" },
                      { v: "all", label: "All" },
                    ].map((opt) => (
                      <label
                        key={opt.v}
                        className={`flex-1 cursor-pointer px-4 py-2 rounded-lg border text-center text-sm transition-colors ${
                          printScope === opt.v
                            ? "bg-blue-900/40 border-blue-500 text-blue-200 font-semibold"
                            : "bg-[#1A1535] border-gray-600 text-gray-300 hover:border-blue-500"
                        }`}
                      >
                        <input
                          type="radio"
                          name="printScope"
                          value={opt.v}
                          checked={printScope === opt.v}
                          onChange={() => setPrintScope(opt.v)}
                          className="hidden"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="mb-2">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-300 font-medium">Fields to include</p>
                  <div className="flex gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => setAllPrintFields(true)}
                      className="text-blue-300 hover:text-blue-200 underline"
                    >
                      Select all
                    </button>
                    <span className="text-gray-600">|</span>
                    <button
                      type="button"
                      onClick={() => setAllPrintFields(false)}
                      className="text-blue-300 hover:text-blue-200 underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  {PRINT_FIELD_GROUPS.map((group) => {
                    const visibleFields = group.fields.filter(
                      (f) => !f.deptOnly || isDepartmentVerified
                    );
                    if (!visibleFields.length) return null;
                    return (
                      <div
                        key={group.title}
                        className="border border-gray-700 rounded-lg p-3 bg-[#1A1535]"
                      >
                        <p className="text-xs font-semibold text-yellow-400 mb-2 uppercase tracking-wide">
                          {group.title}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                          {visibleFields.map((f) => {
                            const checked = selectedPrintFields.has(f.key);
                            return (
                              <label
                                key={f.key}
                                className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer hover:text-white"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => togglePrintField(f.key)}
                                  className="accent-yellow-500"
                                />
                                <span>{f.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowPrintModal(false);
                    if (printScope === "selected") setPrintScope("pending");
                  }}
                  className="px-4 py-2 rounded text-sm font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePrintExport}
                  className="px-4 py-2 rounded text-sm font-medium bg-yellow-500 text-black hover:bg-yellow-600 transition-colors flex items-center gap-2"
                >
                  <Printer className="h-4 w-4" />
                  Print
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Label Manager Modal */}
        <LabelManager
          isOpen={showLabelManager}
          onClose={() => setShowLabelManager(false)}
          labels={labels}
          userId={globalUser?.uid}
          onLabelsUpdate={setLabels}
        />
      </main>

      {/* Share Cases Modal */}
      <ShareCasesModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        selectedCases={selectedCasesForShare}
        onShared={async () => {
          setShowShareModal(false);
          setIsShareSelectionMode(false);
          setSelectedCasesForShare([]);
          setRefreshKey((k) => k + 1);
        }}
      />

      {/* Order notification preference (applies to every case this user owns).
          Reuses the dispose-modal classes so lightMode.css restyles it for free. */}
      {showNotificationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="dispose-modal bg-[#0B0A29] text-white p-6 rounded-lg shadow-xl w-full max-w-sm border border-gray-700">
            <h3 className="dispose-modal-title text-xl font-semibold mb-2 text-yellow-500">
              {orderNotificationsEnabled
                ? "Disable Order Notifications"
                : "Enable Order Notifications"}
            </h3>
            <p className="dispose-modal-text text-sm text-white mb-6">
              {orderNotificationsEnabled
                ? "Do you want to disable notifications for new orders on all your cases? You will stop being alerted when a new order is uploaded."
                : "Notifications for new orders are currently disabled. Do you want to turn them back on for all your cases?"}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowNotificationModal(false)}
                disabled={isSavingNotificationPref}
                className="px-4 py-2 rounded text-sm font-medium bg-white text-black hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleToggleOrderNotifications}
                disabled={isSavingNotificationPref}
                className="px-4 py-2 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {isSavingNotificationPref && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSavingNotificationPref
                  ? "Saving..."
                  : orderNotificationsEnabled
                    ? "Disable"
                    : "Enable"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1535] rounded-xl border border-gray-500 max-w-md w-full p-6">
            <div className="flex flex-col items-center text-center">
              <div className="bg-gray-500 rounded-full p-4 mb-4">
                <Lock size={40} color="white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Case Locked</h2>
              <p className="text-gray-300 mb-4">
                This case was added during your subscription period and is now locked because your subscription has expired.
              </p>
              <div className="bg-[#2d2a5a] rounded-lg p-4 mb-6 w-full">
                <p className="text-sm text-gray-400 mb-2">
                  <span className="font-semibold text-white">Case:</span> {lockedCaseClicked?.caseNumber}
                </p>
                <p className="text-sm text-gray-400">
                  <span className="font-semibold text-white">Party:</span> {lockedCaseClicked?.petitionerName}
                </p>
              </div>
              <p className="text-yellow-400 font-semibold mb-6">
                Upgrade your plan to unlock this case and access all your cases!
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition"
                >
                  Close
                </button>
                <Link
                  to="/portal/subscription"
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2 px-4 rounded-lg transition text-center"
                >
                  View Plans
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="absolute top-0 right-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
        <div className="absolute top-0 right-0 w-3/4 h-3/4 bg-purple-900 rounded-full opacity-10 transform translate-x-1/4 -translate-y-1/4"></div>
        <div className="absolute bottom-0 left-0 w-full h-1/2 bg-purple-900 rounded-full opacity-10 transform -translate-x-1/4 translate-y-1/4"></div>
      </div>
    </div>
  );
}

export default AllCases;